import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { Alert, Badge, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import {
  useTestSlice,
  usePatientInfo,
  useTestActions,
  useTestSnapshot,
  useTestSelector,
  getTestLabel,
  resolveLabel,
} from "context/TestDataContext";
import { useTranslation } from "i18n/LanguageContext";
import CommentDialog from "dialogs/CommentDialog";
import RtfUploadDialog from "dialogs/RtfUploadDialog";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  ceradSemFlZ, ceradBostonZ, ceradMMSZ, ceradSWorterZ,
  ceradTrailAZ, ceradTrailBZ, ceradTrailBAZ,
  ceradFigAbzZ, ceradFigAbrZ, ceradFigSavZ,
  ceradWLLernZ, ceradWLIntruZ, ceradWLAbrufZ, ceradWLSaveZ,
  ceradDiskriZ, genreToSex, roundZ as roundCeradZ,
} from "scoring/calcCERAD";
import { calcColorTrailScore } from "scoring/calcColorTrail";
import { calcEmpanPC } from "scoring/calcEmpan";
import { scoreSpan_DE } from "db/WAIS-IV-DE/wais-iv-de-scoring";
import { calcLabyrinthPC } from "scoring/calcLabyrinth";
import { calcWMSRBlockspanPC } from "scoring/calcWMSRBlockspan";
import { calcStroopVictoria } from "scoring/calcStroopVictoria";
import { calcStroopDkefs } from "scoring/calcStroopDkefs";
import { calcTmtAErreursPC, calcTmtBPersevPC } from "scoring/calcTmtAGrefex";
import { calcBildertestNAI } from "scoring/calcBildertestNAI";
import { calcAmievaRLRI16, centileToPCStr } from "scoring/calcAmievaRLRI16";
import { calcVdlRLRI16, vdlCentileToPCStr, vdlPercentileToPCStr } from "scoring/calcVdlRLRI16";
import { calcBellsRousseaux } from "scoring/calcBellsRousseaux";
import { getTGVPCLabel } from "scoring/calcTGV";
import { calcPortesPC, formatPortesPC } from "scoring/calcPortes";
import { scoreWAIS_ATT_MDT } from "db/WAIS-ATT-MDT/wais-scoring";
import { calcWAISCodeScore, calcWAISCodeIIIScore, calcWAISSymScore, calcWAISSymIIIScore } from "scoring/calcWAISCode";
import { calcRcftMeyers } from "scoring/calcRcftMeyers";
import { calcLGIScore, calcLGIIScore, calcLGIIRecogScore } from "scoring/calcLogischesGedaechtnis";
import { calcNosger } from "scoring/calcNosger";
import { calcBDQ } from "scoring/calcBDQ";
import { fmtSeconds, formatTimeLine, stripSecondsUnit } from "utils/formatSeconds";
import { computeEvaluation, getDtla2017Thresholds, getDtla2017Status, getVospStatus } from "scoring/scoringEval";
import { calcAge } from "utils/dateUtils";
import { SecondaryColoredBorderCard } from "../style/MUIStylePresets";
import { formatGeria12Value } from "utils/geria12";

const EMPAN_LABEL_BY_KIND = { emci: "Empan inverse PC", emcc: "Empan croissant PC", emcd: "Empan direct PC" };
const formatPCRange = (pc) => {
  if (!pc) return null;
  const [low, high] = pc;
  if (low == null && high == null) return null;
  if (low === high) return String(low);
  return `${low ?? "?"}-${high ?? "?"}`;
};


function TestCard({ name, sectionLabel }) {
  const [rtfDialogOpen, setRtfDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const rawTestParams = useTestSlice(name);
  const patientInfo = usePatientInfo();
  const { updateTest, resetTest } = useTestActions();
  const getSnapshot = useTestSnapshot();
  // Cross-test reads gated on this card's test name so unrelated cards don't
  // subscribe (selector returns a stable undefined and never triggers a render).
  const needsStroopA =
    name === "Victoria Stroop, partie B (2009)" ||
    name === "Victoria Stroop, partie C (2009)";
  const needsStroopDkefs13 =
    name === "Test de Stroop, partie 3 (D-KEFS, 2001)" ||
    name === "Test de Stroop, partie 4 (D-KEFS, 2001)";
  const stroopVA_tempsPC = useTestSelector((s) =>
    needsStroopA
      ? s.testData["Victoria Stroop, partie A (2009)"]?.params?.find((p) => p.name === "tempsPC")?.value
      : undefined
  );
  // Sibling stroop's NS row label is "<locale time word> NS" (FR: "Temps NS",
  // DE: "Zeit NS") — match any NS-suffixed row rather than hardcoding FR.
  const stroop1_tempsNS = useTestSelector((s) =>
    needsStroopDkefs13
      ? s.testData["Test de Stroop, partie 1 (D-KEFS, 2001)"]?.resultData?.find((r) => /\sNS$/.test(r.label || ""))?.value
      : undefined
  );
  const stroop3_tempsNS = useTestSelector((s) =>
    needsStroopDkefs13
      ? s.testData["Test de Stroop, partie 3 (D-KEFS, 2001)"]?.resultData?.find((r) => /\sNS$/.test(r.label || ""))?.value
      : undefined
  );
  // CERAD-Plus scales are split across several cards; a few derived z-scores
  // (savings %, intrusions, TMT B/A ratio) need a raw value typed on a
  // sibling card. Same gated cross-test-read pattern as the Stroop reads above.
  const needsCeradLearning = name === "10 mots (CERAD, 2005)";
  const needsCeradAbruf = name === "10 mots (CERAD, 2005) (Row 48)";
  const needsCeradFigRecall = name === "Rappel de figures (CERAD, 2005)";
  const needsCeradTrailB = name === "TMT B (CERAD, 2005 / GREFEX, 2008)";
  const ceradAbrufIntrusions = useTestSelector((s) =>
    needsCeradLearning
      ? parseFloat(s.testData["10 mots (CERAD, 2005) (Row 48)"]?.params?.find((p) => p.name === "intrusions")?.value)
      : undefined
  );
  const ceradLernTrial3 = useTestSelector((s) =>
    needsCeradAbruf
      ? parseFloat(s.testData["10 mots (CERAD, 2005)"]?.params?.find((p) => p.name === "r3")?.value)
      : undefined
  );
  const ceradFigAbzTotal = useTestSelector((s) => {
    if (!needsCeradFigRecall) return undefined;
    const p = s.testData["Copie de figures (CERAD, 2005)"]?.params;
    if (!p) return undefined;
    const v = (n) => parseFloat(p.find((q) => q.name === n)?.value);
    const vals = [v("kreis"), v("rhombus"), v("rechtecke"), v("wuerfel")];
    return vals.some((x) => !Number.isFinite(x)) ? undefined : vals.reduce((s2, x) => s2 + x, 0);
  });
  const ceradTrailATime = useTestSelector((s) =>
    needsCeradTrailB
      ? parseFloat(s.testData["TMT A (CERAD, 2005 / GREFEX, 2008)"]?.params?.find((p) => p.name === "tempsRaw")?.value)
      : undefined
  );
  // Color Trail 2's interference index needs CT1's raw time; read it reactively
  // so CT2 recomputes as soon as CT1 is filled in, not only on its own change.
  const needsCt1Temps = rawTestParams?.trailNumber === 2;
  const ct1TempsLive = useTestSelector((s) =>
    needsCt1Temps
      ? s.testData["Color Trail 1"]?.params?.find((p) => p.name === "temps")?.value
      : undefined
  );
  const { t, lang } = useTranslation();
  const test = rawTestParams;
  const autoCalcTimer = useRef(null);
  const evalGenerationRef = useRef(0);

  // dl: display-label resolver — accepts string or {fr,de} object.
  const dl = (v) => resolveLabel(v, lang);

  // Localised view of test params for *display only*. Handlers that write back
  // to testData must use `rawTestParams.params` so the {fr,de} structure
  // survives across language toggles.
  const testParams = useMemo(() => {
    if (!rawTestParams) return rawTestParams;
    const localizeParam = (p) => ({
      ...p,
      value: dl(p.value),
      label: dl(p.label),
      displayLabel: dl(p.displayLabel),
      placeholder: dl(p.placeholder),
      prefix: dl(p.prefix),
      nsLabel: dl(p.nsLabel),
      scoreLevels: Array.isArray(p.scoreLevels)
        ? p.scoreLevels.map((sl) => ({ ...sl, label: dl(sl.label) }))
        : p.scoreLevels,
    });
    return { ...rawTestParams, params: (rawTestParams.params || []).map(localizeParam) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTestParams, lang]);

  // Warn when norms have an age range and the patient sits outside it
  // (e.g. Praxies Macoir et al., 2025 validated for 50-89 yo).
  const ageBoundWarning = useMemo(() => {
    const minAge = rawTestParams?.minAgeYears;
    const maxAge = rawTestParams?.maxAgeYears;
    if (minAge == null && maxAge == null) return null;
    const age = calcAge(patientInfo.dateNaissance, patientInfo.dateExamen);
    if (age == null) return null;
    const tooYoung = minAge != null && age < minAge;
    const tooOld = maxAge != null && age > maxAge;
    if (!tooYoung && !tooOld) return null;
    const range =
      minAge != null && maxAge != null ? `${minAge}–${maxAge}`
      : minAge != null ? `≥ ${minAge}`
      : `≤ ${maxAge}`;
    const base =
      lang === "de"
        ? `Diese Normen sind nur für Patient:innen ${range} Jahre validiert (aktuelles Alter: ${age}).`
        : `Ces normes ne sont validées que pour les personnes ${range} ans (âge actuel : ${age}).`;
    const altLabel = resolveLabel(rawTestParams?.altTest, lang);
    if (!altLabel) return base;
    return lang === "de"
      ? `${base} Verwenden Sie besser « ${altLabel} ».`
      : `${base} Utilisez plutôt « ${altLabel} ».`;
  }, [rawTestParams, patientInfo.dateNaissance, patientInfo.dateExamen, lang]);

  const handleParamChange = (paramName, value) => {
    const updatedParams = (rawTestParams?.params || []).map((p) =>
      p.name === paramName ? { ...p, value } : p
    );
    updateTest(name, { params: updatedParams });
  };

  const getParamValue = (paramName) =>
    rawTestParams?.params?.find((p) => p.name === paramName)?.value || "";

  const processGeria12XLS = async (parsePromise) => {
    setError(null);
    try {
      const result = await parsePromise;
      if (result.canceled) return;
      if (!result.success) { setError(result.error || t("home.geria.readError")); return; }
      Object.entries(getSnapshot().testData).forEach(([testName, t]) => {
        if (!t?.geria12Cells) return;
        const updatedParams = (t.params || []).map((p) => {
          const cellRef = t.geria12Cells[p.name];
          if (!cellRef) return p;
          const v = result.cells[cellRef];
          if (v == null) return p;
          return { ...p, value: formatGeria12Value(v, p.name) };
        });
        updateTest(testName, { params: updatedParams, geria12File: result.fileName });
      });
    } catch (err) {
      console.error("GERIA-12 XLS upload error:", err);
      setError(err.message);
    }
  };

  const handleUploadGeria12 = () =>
    processGeria12XLS(window.electronAPI.parseGeria12XLS());

  const processNosgerXLS = async (parsePromise) => {
    setError(null);
    try {
      const result = await parsePromise;
      if (result.canceled) return;
      if (!result.success) { setError(result.error || t("home.geria.readError")); return; }
      const calc = calcNosger({
        sums: result.sums,
        thresholds: result.thresholds,
        genderCode: result.genderCode,
        patientGenre: patientInfo.genre,
        lang,
      });
      const { resultData, resultText } = calc;
      // No cross-language fallback — FR row N ≠ DE row N. If the test has no
      // row for this language, skip the evaluation (results still show in card).
      const resultLoc = rawTestParams?.resultLoc?.[lang] ?? null;
      const evaluation = resultLoc
        ? computeEvaluation(resultData, resultLoc, {
            evalRule: "nosger",
            anyDecided: calc.anyDecided,
            anyAuffaellig: calc.anyAuffaellig,
          })
        : null;
      if (result.staleCache) {
        setWarning(lang === "de"
          ? "Alle NOSGER-Summen sind 0 oder leer — die XLSM enthält evtl. veraltete Werte. Bitte in Excel öffnen und speichern."
          : "Toutes les sommes NOSGER sont à 0 ou vides — le fichier contient peut-être des valeurs périmées. Ouvrir et enregistrer dans Excel.");
      } else if (!calc.anyDecided) {
        setWarning(lang === "de"
          ? "Geschlecht nicht gesetzt — NOSGER-Verdikt wurde nicht berechnet."
          : "Genre non renseigné — verdict NOSGER non calculé.");
      }
      updateTest(name, {
        resultData,
        resultText,
        evaluation,
        nosgerFile: result.fileName,
      });
    } catch (err) {
      console.error("NOSGER XLS upload error:", err);
      setError(err.message);
    }
  };

  const handleUploadNosger = () =>
    processNosgerXLS(window.electronAPI.parseNosgerXLS());

  // Whole-card drop target for file-based tests (RTF and Excel imports).
  // dragenter/dragleave fire for every child element, so track depth to avoid
  // flicker while hovering over children.
  const dropKind =
    testParams?.type === "rtf" ? "rtf"
    : testParams?.type === "nosger" ? "nosger"
    : testParams?.type === "bdq" ? "bdq"
    : testParams?.geria12Cells ? "geria12"
    : null;
  const [fileDragging, setFileDragging] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const fileDragDepth = useRef(0);
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");

  const handleFileDragEnter = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    fileDragDepth.current += 1;
    setFileDragging(true);
  };

  const handleFileDragOver = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleFileDragLeave = (e) => {
    if (!hasFiles(e)) return;
    fileDragDepth.current = Math.max(0, fileDragDepth.current - 1);
    if (fileDragDepth.current === 0) setFileDragging(false);
  };

  const DROP_EXTS = {
    rtf: [".rtf"],
    geria12: [".xls"],
    nosger: [".xlsm", ".xlsx", ".xls"],
    bdq: [".xlsx", ".xlsm", ".xls"],
  };

  const parseDroppedRtf = async (file) => {
    // Pass the File object — Electron preload extracts .path, web shim uploads.
    const result = await window.electronAPI.parseDroppedRTFFile(file, lang);
    if (!result.success) { setError(result.error || "Failed to parse RTF file"); return; }
    const { testType, contextData } = result.data;
    if (!contextData) { setError(`No mapping config for test type: ${testType}`); return; }
    const evaluation = computeEvaluation(contextData.resultData, contextData.resultLoc);
    updateTest(name, { ...contextData, evaluation });
  };

  const handleDropFile = async (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    e.stopPropagation();
    fileDragDepth.current = 0;
    setFileDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file || !dropKind) return;
    setError(null);
    const exts = DROP_EXTS[dropKind];
    if (!exts.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      const list = exts.join(", ");
      setError(lang === "de"
        ? `Bitte eine gültige Datei ablegen (${list})`
        : `Veuillez déposer un fichier valide (${list})`);
      return;
    }
    setFileLoading(true);
    try {
      if (dropKind === "rtf") await parseDroppedRtf(file);
      else if (dropKind === "geria12") await processGeria12XLS(window.electronAPI.parseGeria12XLSFromPath(file));
      else if (dropKind === "nosger") await processNosgerXLS(window.electronAPI.parseNosgerXLSFromPath(file));
      else if (dropKind === "bdq") await processBdqXLS(window.electronAPI.parseBdqXLSFromPath(file));
    } catch (err) {
      console.error("File drop error:", err);
      setError(err.message);
    } finally {
      setFileLoading(false);
    }
  };

  // Hint shown next to the upload button inside the dashed drop zone.
  const dropHint = (label) => (
    fileLoading ? (
      <CircularProgress size={18} />
    ) : (
      <Typography variant="caption" color={fileDragging ? "primary" : "textSecondary"}>
        {fileDragging
          ? (lang === "de" ? `${label} hier ablegen` : `Déposer le ${label} ici`)
          : (lang === "de" ? "oder hier ablegen" : "ou déposer ici")}
      </Typography>
    )
  );

  const dropZoneSx = {
    display: "flex", alignItems: "center", gap: 1,
    border: "1px dashed", borderColor: fileDragging ? "primary.main" : "divider", borderRadius: 2,
    px: 1.5, py: 0.75,
    transition: "border-color 0.2s ease-in-out",
  };

  const processBdqXLS = async (parsePromise) => {
    setError(null);
    try {
      const result = await parsePromise;
      if (result.canceled) return;
      if (!result.success) { setError(result.error || t("home.geria.readError")); return; }
      const calc = calcBDQ({
        scores: result.scores,
        total: result.total,
        patientGenre: patientInfo.genre,
        lang,
      });
      const { resultData, resultText } = calc;
      const resultLoc = rawTestParams?.resultLoc?.[lang] ?? null;
      const evaluation = resultLoc
        ? computeEvaluation(resultData, resultLoc, {
            evalRule: "bdq",
            anyDecided: calc.anyDecided,
            anyAuffaellig: calc.anyAuffaellig,
          })
        : null;
      if (!calc.anyDecided) {
        setWarning(lang === "de"
          ? "Geschlecht nicht gesetzt — BDQ-Verdikt wurde nicht berechnet."
          : "Genre non renseigné — verdict BDQ non calculé.");
      }
      updateTest(name, {
        resultData,
        resultText,
        evaluation,
        bdqFile: result.fileName,
      });
    } catch (err) {
      console.error("BDQ XLS upload error:", err);
      setError(err.message);
    }
  };

  const handleUploadBdq = () =>
    processBdqXLS(window.electronAPI.parseBdqXLS());

  const handleCalculer = async () => {
    setError(null);
    setWarning(null);
    try {
      const age = calcAge(patientInfo.dateNaissance, patientInfo.dateExamen);
      if (age === null) {
        setError(lang === "de" ? "Geburtsdatum fehlt" : "Date de naissance manquante");
        return;
      }

      let result;

      if (testParams.trailNumber) {
        // Color Trail scoring
        const temps = getParamValue("temps");
        const erreurs = getParamValue("erreurs");
        if (!temps) { setError(lang === "de" ? "Bitte Zeit eingeben" : "Veuillez entrer le temps"); return; }
        const edu = parseFloat(patientInfo.anneesEtudes);
        if (isNaN(edu)) { setError(t("test.errorYears")); return; }
        const ct1Temps = testParams.trailNumber === 2
          ? getSnapshot().testData["Color Trail 1"]?.params?.find((p) => p.name === "temps")?.value
          : null;
        result = await calcColorTrailScore(testParams.trailNumber, temps, erreurs || "0", age, edu, ct1Temps, lang);
      } else if (["waisCode", "waisCodeIII", "waisSym", "waisSymIII"].includes(testParams.calcType)) {
        const rawScore = getParamValue("rawScore");
        if (!rawScore) { setError(lang === "de" ? "Bitte Rohwert eingeben" : "Veuillez entrer le score brut"); return; }
        const raw = parseInt(rawScore);
        if (testParams.calcType === "waisCode") result = await calcWAISCodeScore(raw, age, lang);
        else if (testParams.calcType === "waisCodeIII") result = calcWAISCodeIIIScore(raw, age);
        else if (testParams.calcType === "waisSym") result = await calcWAISSymScore(raw, age, lang);
        else if (testParams.calcType === "waisSymIII") result = calcWAISSymIIIScore(raw, age);
      } else if (["lgI", "lgII", "lgIIRecog"].includes(testParams.calcType)) {
        const rawScore = getParamValue("rawScore");
        if (rawScore === "" || rawScore == null) { setError(lang === "de" ? "Bitte Rohwert eingeben" : "Veuillez entrer le score brut"); return; }
        const raw = parseInt(rawScore);
        const activeVersion = resolveActiveVersion();
        const version = activeVersion?.version || testParams.version || "ab";
        if (testParams.calcType === "lgI") result = await calcLGIScore(raw, age, version);
        else if (testParams.calcType === "lgII") result = await calcLGIIScore(raw, age, version);
        else if (testParams.calcType === "lgIIRecog") result = await calcLGIIRecogScore(raw, age, version);
      } else if (testParams.calcType === "rcftCopie" || testParams.calcType === "rcftImmediat" || testParams.calcType === "rcftDiffere") {
        const input = { age, lang };
        if (testParams.calcType === "rcftCopie") {
          input.copy = getParamValue("copyRaw");
          input.copyTime = getParamValue("timeRaw");
          if (!input.copy && !input.copyTime) {
            setError(lang === "de" ? "Bitte Kopie-Punktzahl oder Zeit eingeben" : "Veuillez entrer le score de copie ou le temps");
            return;
          }
        } else if (testParams.calcType === "rcftImmediat") {
          input.immediateRecall = getParamValue("immediateRaw");
          if (!input.immediateRecall) {
            setError(t("test.errorImmediate"));
            return;
          }
        } else {
          input.delayedRecall = getParamValue("delayedRaw");
          if (!input.delayedRecall) {
            setError(t("test.errorDelayed"));
            return;
          }
        }
        result = await calcRcftMeyers(input);
      }

      if (!result) return;
      if (result.error) { setError(result.error); return; }
      if (result.warning) setWarning(result.warning);

      // Multi-row results (CERAD-style): array of {resultLoc, resultData, ...}
      if (Array.isArray(result.entries)) {
        updateTest(name, { results: result.entries });
        return;
      }

      // Calc functions that pick a row dynamically (WMS-IV LG by booklet) return a
      // single-string resultLoc — merge into the entry's {fr,de} shape so scoreFillUtils
      // writes the booklet-specific row instead of the static default.
      const updates = {
        resultData: result.resultData,
        resultText: result.resultText,
        evaluation: result.evaluation,
      };
      if (typeof result.resultLoc === "string" && result.resultLoc) {
        const baseLoc = rawTestParams?.resultLoc;
        updates.resultLoc = (baseLoc && typeof baseLoc === "object")
          ? { ...baseLoc, [lang]: result.resultLoc }
          : { [lang]: result.resultLoc };
      }
      updateTest(name, updates);
    } catch (err) {
      console.error("Scoring error:", err);
      setError(err.message);
    }
  };

  // Returns the FR-flavoured location string used as input to computeEvaluation
  // (row math is language-invariant). scoreFillUtils translates the row to the
  // active language at fill time via frToDeRow.
  const pickLocStr = (locField) => {
    if (!locField) return locField;
    if (typeof locField === "string") return locField;
    return locField.fr ?? locField.de ?? null;
  };
  const resolveResultLoc = () => {
    if (!testParams.ageThresholds) return pickLocStr(testParams.resultLoc);
    const age = calcAge(patientInfo.dateNaissance, patientInfo.dateExamen);
    for (const threshold of testParams.ageThresholds) {
      if (threshold.maxAge === undefined || (age !== null && age <= threshold.maxAge)) {
        return pickLocStr(threshold.resultLoc);
      }
    }
    return pickLocStr(testParams.ageThresholds[testParams.ageThresholds.length - 1].resultLoc);
  };

  // Tests with `ageNorms` share one scoresheet row but pick the norm (evalRule +
  // cutoff) by patient age. Returns the active norm entry, or null if age unknown.
  const resolveActiveNorm = () => {
    if (!testParams.ageNorms) return null;
    const age = calcAge(patientInfo.dateNaissance, patientInfo.dateExamen);
    if (age === null) return null;
    for (const n of testParams.ageNorms) {
      if (n.maxAge === undefined || age <= n.maxAge) return n;
    }
    return testParams.ageNorms[testParams.ageNorms.length - 1];
  };

  // Tests with `versionByAge` (WMS-IV LG) pick a booklet variant by age.
  // Returns the active entry { version, label, minAge?, maxAge? } or null
  // when age is unknown or outside every entry's range.
  const resolveActiveVersion = () => {
    if (!testParams.versionByAge) return null;
    const age = calcAge(patientInfo.dateNaissance, patientInfo.dateExamen);
    if (age === null) return null;
    for (const v of testParams.versionByAge) {
      const minOk = v.minAge === undefined || age >= v.minAge;
      const maxOk = v.maxAge === undefined || age <= v.maxAge;
      if (minOk && maxOk) return v;
    }
    return null;
  };

  const handleManualEval = async () => {
    const gen = ++evalGenerationRef.current;
    try {
      const resultLoc = resolveResultLoc();
      const params = testParams.params || [];
      const age = calcAge(patientInfo.dateNaissance, patientInfo.dateExamen);

      const empanResults = {};
      const subtestResults = {};
      const labyrinthResults = {};
      const wmsrBlockspanResults = {};
      const stroopDkefsResults = {};
      const tmtAErreursResults = {};
      const tmtBPersevResults = {};
      let stroopV = null;

      const stroopVParams = params.filter((p) => p.type === "stroopVRaw");
      if (stroopVParams.length > 0) {
        const niveau = patientInfo.niveau;
        const nsc = niveau === "I" ? 1 : (niveau === "II" || niveau === "III") ? 2 : null;
        if (age != null && nsc != null) {
          const valByKind = (kind) => stroopVParams.find((p) => p.kind === kind)?.value;
          const aTemps = parseFloat(getSnapshot().testData["Victoria Stroop, partie A (2009)"]?.params?.find((p) => p.name === "tempsPC")?.value);
          const ownCouleurs = valByKind("tCouleurs");
          stroopV = calcStroopVictoria({
            age,
            nsc,
            tCouleurs: ownCouleurs ? parseFloat(ownCouleurs) : (Number.isFinite(aTemps) ? aTemps : undefined),
            tMots: valByKind("tMots") ? parseFloat(valByKind("tMots")) : undefined,
            tInterference: valByKind("tInterference") ? parseFloat(valByKind("tInterference")) : undefined,
            errCouleurs: valByKind("errCouleurs"),
            errMots: valByKind("errMots"),
            errInterfTotal: valByKind("errInterfTotal"),
            errInterfNonCorr: valByKind("errInterfNonCorr"),
            errInterfCorr: valByKind("errInterfCorr"),
          });
        }
      }

      const empanParams = params.filter((p) => p.type === "empanRaw" && p.value);
      await Promise.all(
        empanParams.map(async (p) => {
          try {
            const raw = parseInt(p.value, 10);
            if (isNaN(raw) || age === null) return;
            const r = lang === "de"
              ? await scoreSpan_DE(age, raw, p.kind)
              : await calcEmpanPC(age, raw, p.kind);
            if (!r || r.error || !r.pc) return;
            empanResults[p.name] = r;
          } catch (e) {
            console.error(`calcEmpanPC error (${p.kind}, ${p.value}):`, e);
          }
        })
      );

      const labyrinthParams = params.filter((p) => p.type === "labyrinthTempsRaw" && p.value);
      await Promise.all(
        labyrinthParams.map(async (p) => {
          try {
            if (age === null) {
              labyrinthResults[p.name] = {
                error: lang === "de" ? "Geburtsdatum fehlt" : "Date de naissance manquante",
              };
              return;
            }
            const r = await calcLabyrinthPC(age, p.value, lang);
            if (!r) return;
            labyrinthResults[p.name] = r;
          } catch (e) {
            console.error(`calcLabyrinthPC error (${p.value}):`, e);
            labyrinthResults[p.name] = { error: e.message };
          }
        })
      );

      const wmsrBlockspanParams = params.filter((p) => p.type === "wmsrBlockspanRaw" && p.value);
      await Promise.all(
        wmsrBlockspanParams.map(async (p) => {
          try {
            if (age === null) {
              wmsrBlockspanResults[p.name] = {
                error: lang === "de" ? "Geburtsdatum fehlt" : "Date de naissance manquante",
              };
              return;
            }
            const r = await calcWMSRBlockspanPC(age, p.value, p.kind, lang);
            if (!r) return;
            wmsrBlockspanResults[p.name] = r;
          } catch (e) {
            console.error(`calcWMSRBlockspanPC error (${p.kind}, ${p.value}):`, e);
            wmsrBlockspanResults[p.name] = { error: e.message };
          }
        })
      );

      const tmtAErreursParams = params.filter((p) => p.type === "tmtAErreursRaw" && p.value);
      await Promise.all(
        tmtAErreursParams.map(async (p) => {
          try {
            const r = await calcTmtAErreursPC(age, patientInfo.niveau, p.value, lang);
            if (!r) return;
            tmtAErreursResults[p.name] = r;
          } catch (e) {
            console.error(`calcTmtAErreursPC error (${p.value}):`, e);
            tmtAErreursResults[p.name] = { error: e.message };
          }
        })
      );

      const tmtBPersevParams = params.filter((p) => p.type === "tmtBPersevRaw" && p.value);
      await Promise.all(
        tmtBPersevParams.map(async (p) => {
          try {
            const r = await calcTmtBPersevPC(age, patientInfo.niveau, p.value, lang);
            if (!r) return;
            tmtBPersevResults[p.name] = r;
          } catch (e) {
            console.error(`calcTmtBPersevPC error (${p.value}):`, e);
            tmtBPersevResults[p.name] = { error: e.message };
          }
        })
      );

      const portesParams = params.filter((p) => p.type === "portesRaw");
      const portesResults = {};
      let portesTotal = null;
      if (portesParams.length > 0) {
        const partieA = portesParams.find((p) => p.kind === "A");
        const partieB = portesParams.find((p) => p.kind === "B");
        for (const p of portesParams) {
          if (!p.value) continue;
          const v = calcPortesPC(p.value, age, p.kind);
          if (v != null) portesResults[p.name] = { pc: v, raw: p.value };
        }
        const rawA = parseFloat(partieA?.value);
        const rawB = parseFloat(partieB?.value);
        if (!isNaN(rawA) && !isNaN(rawB)) {
          const totalRaw = rawA + rawB;
          const totalPC = calcPortesPC(totalRaw, age, "AB");
          if (totalPC != null) portesTotal = { pc: totalPC, raw: String(totalRaw) };
        }
      }

      const amievaRawParams = params.filter((p) => p.type === "amievaRaw");
      const isAmievaEntry = amievaRawParams.length > 0 && !!testParams.amievaResultLocs;
      if (isAmievaEntry) {
        const niveau = patientInfo.niveau === "I" ? 1
          : (patientInfo.niveau === "II" || patientInfo.niveau === "III") ? 2
          : null;
        const gender = patientInfo.genre;
        // FR-flavoured locs feed computeEvaluation; scoreFillUtils translates rows at fill time.
        const locs = testParams.amievaResultLocs.fr || testParams.amievaResultLocs;
        const commentParam = params.find((p) => p.name === "commentaire");
        const commentValue = commentParam?.value || "";
        const hasAnyValue = amievaRawParams.some((p) => p.value !== "" && p.value != null);
        const amievaResults = {};
        const results = [];
        if (hasAnyValue && age != null && niveau != null && (gender === "H" || gender === "F")) {
          const input = { gender, age, niveau };
          for (const p of amievaRawParams) input[p.name] = p.value;
          const r = calcAmievaRLRI16(input);
          if (!r.error && r.scores) {
            for (const [k, v] of Object.entries(r.scores)) {
              amievaResults[k] = { centile: v.centile, brut: v.brut, label: v.label };
            }
            const cBin = (k) => centileToPCStr(r.scores[k]?.centile);
            const buildLoc = (resultLoc, items) => {
              const filtered = items.filter((d) => d.value);
              if (filtered.length === 0) return null;
              const resultText = filtered.map((d) => `${d.label}= ${d.value}`).join("\n");
              const evaluation = computeEvaluation(filtered, resultLoc);
              return { resultLoc, resultData: filtered, resultText, evaluation };
            };
            const isDe = lang === "de";
            const enc = buildLoc(locs.encodage, [
              { name: "rimPC", label: isDe ? "Unmittelb. Abruf PC" : "RIM PC", value: cBin("rappelImmediat") },
              { name: "rappelsTotauxPC", label: isDe ? "Gesamtabrufe PC" : "Rappels totaux PC", value: cBin("totalRappelsLibresIndices") },
              { name: "recuperationPC", label: isDe ? "% Hinweisgewinn PC" : "% récupération par indiçage PC", value: cBin("pctRecuperationIndices") },
            ]);
            const rl = buildLoc(locs.rappelLibre, [
              { name: "rappelLibrePC", label: isDe ? "Freier Abruf PC" : "Rappel libre PC", value: cBin("rappelDiffereLibre") },
            ]);
            const rt = buildLoc(locs.rappelTotal, [
              { name: "rappelTotalPC", label: isDe ? "Gesamtabruf PC" : "Rappel total PC", value: cBin("totalRappelDiffere") },
              { name: "recuperationPC", label: isDe ? "% Erinnerung PC" : "% récupération PC", value: cBin("pctRecuperationDiffere") },
            ]);
            for (const x of [enc, rl, rt]) if (x) results.push(x);
          }
        }
        if (commentValue) {
          const cmData = [{ name: "comment", label: "comment", value: commentValue }];
          results.push({
            resultLoc: locs.commentaire,
            resultData: cmData,
            resultText: commentValue,
            evaluation: computeEvaluation(cmData, locs.commentaire),
          });
        }
        if (gen !== evalGenerationRef.current) return;
        updateTest(name, { results: results.length > 0 ? results : null, amievaResults });
        return;
      }

      const vdlRawParams = params.filter((p) => p.type === "vdlRaw");
      const isVdlEntry = vdlRawParams.length > 0 && !!testParams.vdlResultLocs;
      if (isVdlEntry) {
        const nsc = patientInfo.niveau === "I" ? 1
          : patientInfo.niveau === "II" ? 2
          : patientInfo.niveau === "III" ? 3
          : null;
        const gender = patientInfo.genre;
        // FR-flavoured locs feed computeEvaluation; scoreFillUtils translates rows at fill time.
        const locs = testParams.vdlResultLocs.fr || testParams.vdlResultLocs;
        const commentParam = params.find((p) => p.name === "commentaire");
        const commentValue = commentParam?.value || "";
        const hasAnyValue = vdlRawParams.some((p) => p.value !== "" && p.value != null);
        const vdlResults = {};
        const results = [];
        if (hasAnyValue && age != null && nsc != null && (gender === "H" || gender === "F")) {
          const input = { gender, age, nsc };
          for (const p of vdlRawParams) input[p.name] = p.value;
          const r = calcVdlRLRI16(input);
          if (!r.error && r.scores) {
            for (const [k, v] of Object.entries(r.scores)) vdlResults[k] = v;
            const pcLibre = (k) => {
              const sc = r.scores[k];
              return sc?.percentile != null ? vdlPercentileToPCStr(sc.percentile) : "";
            };
            const pcTotal = (k) => {
              const sc = r.scores[k];
              return sc?.centile ? vdlCentileToPCStr(sc.centile) : "";
            };
            const buildLoc = (resultLoc, items) => {
              const filtered = items.filter((d) => d.value);
              if (filtered.length === 0) return null;
              const resultText = filtered.map((d) => `${d.label}= ${d.value}`).join("\n");
              const evaluation = computeEvaluation(filtered, resultLoc);
              return { resultLoc, resultData: filtered, resultText, evaluation };
            };
            const enc = buildLoc(locs.encodage, [
              { name: "rl1PC", label: "RL1 PC", value: pcLibre("RL1") },
              { name: "rl2PC", label: "RL2 PC", value: pcLibre("RL2") },
              { name: "rl3PC", label: "RL3 PC", value: pcLibre("RL3") },
            ]);
            const rim = buildLoc(locs.rappelImmediat, [
              { name: "rimPC", label: "RIM PC", value: pcTotal("RIM") },
              { name: "rt1PC", label: "RT1 PC", value: pcTotal("RT1") },
              { name: "rt2PC", label: "RT2 PC", value: pcTotal("RT2") },
              { name: "rt3PC", label: "RT3 PC", value: pcTotal("RT3") },
            ]);
            const rl = buildLoc(locs.rappelLibre, [
              { name: "rldPC", label: "RLD PC", value: pcLibre("RLD") },
            ]);
            const ri = buildLoc(locs.rappelIndice, [
              { name: "rtdPC", label: "RTD PC", value: pcTotal("RTD") },
            ]);
            for (const x of [enc, rim, rl, ri]) if (x) results.push(x);
          }
        }
        if (commentValue) {
          const cmData = [{ name: "comment", label: "comment", value: commentValue }];
          results.push({
            resultLoc: locs.commentaire,
            resultData: cmData,
            resultText: commentValue,
            evaluation: computeEvaluation(cmData, locs.commentaire),
          });
        }
        if (gen !== evalGenerationRef.current) return;
        updateTest(name, { results: results.length > 0 ? results : null, vdlResults });
        return;
      }

      let bellsResult = null;
      if (testParams.evalRule === "bellsRousseaux") {
        const bellsParams = params.filter((p) => p.type === "bellsRaw");
        const get = (kind) => bellsParams.find((p) => p.kind === kind)?.value;
        bellsResult = calcBellsRousseaux({
          age,
          niveau: patientInfo.niveau,
          omG: get("omG"),
          omD: get("omD"),
          erreurs: get("erreurs"),
          temps: get("temps"),
        });
      }

      const bildertestResults = {};
      const bildertestWertParams = params.filter((p) => p.type === "bildertestWertRaw" && p.value);
      const bildertestSetting = params.find((p) => p.type === "bildertestSettingToggle")?.value || "haushalt";
      await Promise.all(
        bildertestWertParams.map(async (p) => {
          try {
            const r = await calcBildertestNAI(age, bildertestSetting, p.value, lang);
            if (!r) return;
            bildertestResults[p.name] = r;
          } catch (e) {
            console.error(`calcBildertestNAI error (${p.value}):`, e);
            bildertestResults[p.name] = { error: e.message };
          }
        })
      );

      const isDiffKind = (k) => k === "cond31Diff" || k === "cond43Diff";
      const stroopDkefsRegular = params.filter((p) => p.type === "stroopDkefsRaw" && !isDiffKind(p.kind) && p.value);
      const stroopDkefsDiffs   = params.filter((p) => p.type === "stroopDkefsRaw" && isDiffKind(p.kind));

      await Promise.all(
        stroopDkefsRegular.map(async (p) => {
          try {
            if (age === null) {
              stroopDkefsResults[p.name] = {
                error: lang === "de" ? "Geburtsdatum fehlt" : "Date de naissance manquante",
              };
              return;
            }
            const r = await calcStroopDkefs({ age, raw: p.value, kind: p.kind, lang });
            if (!r) return;
            stroopDkefsResults[p.name] = r;
          } catch (e) {
            console.error(`calcStroopDkefs error (${p.kind}, ${p.value}):`, e);
            stroopDkefsResults[p.name] = { error: e.message };
          }
        })
      );

      // Pass 2: D.6 contrast — needs own time NS (just computed) + sibling test's NS.
      // The sibling's resultData label is built from the sibling param's `p.label`
      // stripped of its seconds unit then suffixed " NS" (see stroopDkefsRaw push above), so
      // we match against either localization rather than hardcoding FR.
      const DIFF_DEPS = {
        cond31Diff: { ownKind: "cond3Time", otherTest: "Test de Stroop, partie 1 (D-KEFS, 2001)" },
        cond43Diff: { ownKind: "cond4Time", otherTest: "Test de Stroop, partie 3 (D-KEFS, 2001)" },
      };
      const NS_LABEL_RX = /\sNS$/;
      for (const p of stroopDkefsDiffs) {
        const meta = DIFF_DEPS[p.kind];
        const ownParam = params.find((q) => q.kind === meta.ownKind);
        const ownNS = ownParam ? stroopDkefsResults[ownParam.name]?.ns : null;
        const snapshotTestData = getSnapshot().testData;
        const otherEntry = snapshotTestData[meta.otherTest]?.resultData?.find((r) => NS_LABEL_RX.test(r.label || ""));
        const otherNS = otherEntry ? parseInt(otherEntry.value, 10) : NaN;
        const otherLabelDisplay = getTestLabel(meta.otherTest, snapshotTestData[meta.otherTest], lang);
        const ownLabelDisplay = ownParam?.label ? stripSecondsUnit(ownParam.label) : (lang === "de" ? "Zeit" : "Temps");
        const quote = lang === "de" ? ["„", '"'] : ["« ", " »"];

        if (!Number.isFinite(ownNS) && !Number.isFinite(otherNS)) {
          stroopDkefsResults[p.name] = {
            error: lang === "de"
              ? `${ownLabelDisplay} und ${quote[0]}${otherLabelDisplay}${quote[1]} angeben`
              : `Renseignez ${ownLabelDisplay} et ${quote[0]}${otherLabelDisplay}${quote[1]}`,
          };
          continue;
        }
        if (!Number.isFinite(ownNS)) {
          stroopDkefsResults[p.name] = {
            error: lang === "de"
              ? `${ownLabelDisplay} angeben`
              : `Renseignez ${ownLabelDisplay}`,
          };
          continue;
        }
        if (!Number.isFinite(otherNS)) {
          stroopDkefsResults[p.name] = {
            error: lang === "de"
              ? `${quote[0]}${otherLabelDisplay}${quote[1]} fehlt`
              : `${quote[0]}${otherLabelDisplay}${quote[1]} manquant`,
          };
          continue;
        }

        try {
          const r = await calcStroopDkefs({ raw: ownNS - otherNS, kind: p.kind, lang });
          if (!r) continue;
          stroopDkefsResults[p.name] = r;
        } catch (e) {
          console.error(`calcStroopDkefs diff error (${p.kind}):`, e);
          stroopDkefsResults[p.name] = { error: e.message };
        }
      }

      for (const p of params) {
        if (p.type !== "subtestRaw" || !p.value) continue;
        try {
          const raw = parseInt(p.value, 10);
          if (isNaN(raw) || age === null) continue;
          const r = scoreWAIS_ATT_MDT(age, { [p.kind]: raw }, p.kind);
          const sub = r?.subtests?.[p.kind];
          if (!sub || sub.error || sub.ns == null) continue;
          subtestResults[p.name] = sub;
        } catch (e) {
          console.error(`scoreWAIS_ATT_MDT error (${p.kind}, ${p.value}):`, e);
        }
      }

      // --- CERAD-Plus raw-input z-scores ---
      // Cards with `ceradScale` collect raw subtest scores as plain "input"
      // params (marked excludeFromResult so they don't get pushed verbatim
      // below) and compute the age/edu/sex-adjusted z here. A few scales need
      // a raw value from a sibling card (see the cross-test reads above).
      const ceradResultData = [];
      if (testParams.ceradScale) {
        const edu = parseFloat(patientInfo.anneesEtudes);
        const sex = genreToSex(patientInfo.genre);
        const ctx = { age, edu, sex };
        const num = (n) => {
          const v = params.find((q) => q.name === n)?.value;
          return v === "" || v == null ? NaN : parseFloat(v);
        };
        const pushZ = (nm, label, z, italic, rawTemps) => {
          if (z == null) return;
          ceradResultData.push({ name: nm, label, value: String(roundCeradZ(z)), ...(italic ? { italic: true } : {}), ...(Number.isFinite(rawTemps) ? { rawTemps } : {}) });
        };
        switch (testParams.ceradScale) {
          case "SemFl":
            pushZ("z", "z", ceradSemFlZ(num("total"), ctx));
            break;
          case "SWorter":
            pushZ("z", "z", ceradSWorterZ(num("total"), ctx));
            break;
          case "Boston":
            pushZ("z", "z", ceradBostonZ(num("total"), ctx));
            break;
          case "MMS": {
            const total = num("total");
            if (Number.isFinite(total)) ceradResultData.push({ name: "comment", label: "comment", value: `${total}/30` });
            pushZ("z", "z", ceradMMSZ(total, ctx));
            break;
          }
          case "WL_Lern": {
            const { z } = ceradWLLernZ(num("r1"), num("r2"), num("r3"), ctx);
            const intruZ = ceradWLIntruZ(num("i1"), num("i2"), num("i3"), ceradAbrufIntrusions, ctx);
            pushZ("lernZ", "Σ Essais1-3 z", z);
            pushZ("intruZ", "Intrusions z", intruZ, true);
            break;
          }
          case "WL_Abruf": {
            const richtige = num("richtige");
            pushZ("abrufZ", "Score brut z", ceradWLAbrufZ(richtige, ctx));
            pushZ("saveZ", "Rétention z", ceradWLSaveZ(richtige, ceradLernTrial3, ctx));
            break;
          }
          case "Diskri":
            pushZ("z", "Discriminabilité z", ceradDiskriZ(num("richtigeJa"), num("richtigeNein"), ctx));
            break;
          case "Fig_abz": {
            const { z } = ceradFigAbzZ(num("kreis"), num("rhombus"), num("rechtecke"), num("wuerfel"), ctx);
            pushZ("z", "z", z);
            break;
          }
          case "Fig_abr": {
            const { total, z } = ceradFigAbrZ(num("item1"), num("item2"), num("item3"), num("item4"), ctx);
            pushZ("abrZ", "Score brut z", z);
            pushZ("savZ", "Rétention z", ceradFigSavZ(total, ceradFigAbzTotal, ctx));
            break;
          }
          case "Trail_A":
            pushZ("trailAZ", "Temps z", ceradTrailAZ(num("tempsRaw"), ctx), false, num("tempsRaw"));
            break;
          case "Trail_B": {
            const timeB = num("tempsRaw");
            pushZ("trailBZ", "Temps z", ceradTrailBZ(timeB, ctx), false, timeB);
            const trailBA = ceradTrailBAZ(ceradTrailATime, timeB, ctx);
            if (trailBA != null) {
              pushZ("trailBAZ", "B/A z", trailBA, true);
            } else if (Number.isFinite(age) && Number.isFinite(edu) && (sex === 0 || sex === 1)) {
              // Age/edu/sex are known, so the only reason the ratio is missing
              // is one of the two raw times — same dependency note as the
              // Stroop interference indices.
              const missing = [];
              if (!Number.isFinite(ceradTrailATime) || ceradTrailATime <= 0) missing.push(lang === "de" ? "Zeit TMT A" : "Temps TMT A");
              if (!Number.isFinite(timeB)) missing.push(lang === "de" ? "Zeit TMT B" : "Temps TMT B");
              if (missing.length > 0) {
                ceradResultData.push({
                  name: "trailBAZ",
                  label: "B/A z",
                  value: lang === "de" ? `${missing.join(" und ")} angeben` : `Renseignez ${missing.join(" et ")}`,
                  italic: true,
                  error: true,
                });
              }
            }
            break;
          }
          default:
            break;
        }
      }

      const resultData = [...ceradResultData];
      for (const p of params) {
        if (p.excludeFromResult) continue;
        if (p.type === "empanRaw") {
          const r = empanResults[p.name];
          if (!r) continue;
          const pcStr = formatPCRange(r.pc);
          if (pcStr == null) continue;
          const stabilityParam = params.find((q) => q.type === "stability" && q.linkedTo === p.name);
          resultData.push({
            name: p.name,
            label: EMPAN_LABEL_BY_KIND[p.kind] || `${p.label} PC`,
            value: pcStr,
            rawEmpan: p.value,
            stability: stabilityParam?.value || "",
          });
        } else if (p.type === "subtestRaw") {
          const sub = subtestResults[p.name];
          if (!sub) continue;
          resultData.push({
            name: p.name,
            label: p.nsLabel || `${p.kind.toUpperCase()} NS`,
            value: String(sub.ns),
            rawSubtest: p.value,
          });
        } else if (p.type === "labyrinthTempsRaw") {
          const r = labyrinthResults[p.name];
          if (!r) continue;
          resultData.push({
            name: p.name,
            label: lang === "de" ? "Zeit PC" : "Temps PC",
            value: r.pr,
            rawTemps: p.value,
          });
        } else if (p.type === "wmsrBlockspanRaw") {
          const r = wmsrBlockspanResults[p.name];
          if (!r || r.error || r.pc == null) continue;
          resultData.push({
            name: p.name,
            label: lang === "de" ? "Gesamtrohwert PC" : "Score global PC",
            value: String(r.pc),
            rawWmsrGlobal: p.value,
          });
        } else if (p.type === "stroopDkefsRaw") {
          const r = stroopDkefsResults[p.name];
          if (!r || r.error) continue;
          const isDiff = p.kind === "cond31Diff" || p.kind === "cond43Diff";
          if (r.ns != null) {
            resultData.push({
              name: p.name,
              label: isDiff ? p.label : `${stripSecondsUnit(p.label)} NS`,
              value: String(r.ns),
              ...(isDiff ? {} : { rawTemps: p.value }),
            });
          } else if (r.pc != null) {
            const isZeroErrors = parseInt(p.value, 10) === 0;
            resultData.push({
              name: p.name,
              label: `${p.label} PC`,
              value: isZeroErrors ? "> 10" : String(r.pc),
              rawValue: isZeroErrors ? 50 : r.pc,
              rawErreurs: p.value,
              atCap: !!r.atCap,
            });
          }
        } else if (p.type === "tmtAErreursRaw") {
          const r = tmtAErreursResults[p.name];
          if (!r || r.error || !r.pc) continue;
          resultData.push({
            name: p.name,
            label: lang === "de" ? "Fehler PC" : "Erreurs PC",
            value: r.pc,
            rawErreurs: p.value,
            italic: true,
          });
        } else if (p.type === "tmtBPersevRaw") {
          const r = tmtBPersevResults[p.name];
          if (!r || r.error || !r.pc) continue;
          resultData.push({
            name: p.name,
            label: lang === "de" ? "Perseverationen PC" : "Persévérations PC",
            value: r.pc,
            rawValue: p.value,
            italic: true,
          });
        } else if (p.type === "portesRaw") {
          const r = portesResults[p.name];
          if (!r) continue;
          const partLabel = lang === "de"
            ? (p.kind === "A" ? "Teil A PC" : "Teil B PC")
            : (p.kind === "A" ? "Partie A PC" : "Partie B PC");
          const sheetName = p.kind === "A" ? "partieAPC" : "partieBPC";
          resultData.push({
            name: sheetName,
            label: partLabel,
            value: r.pc,
            rawPortes: r.raw,
            kind: "portes",
          });
        } else if (p.type === "bildertestWertRaw") {
          const r = bildertestResults[p.name];
          if (!r || r.error || !r.pr) continue;
          resultData.push({
            name: p.name,
            label: "PC",
            value: r.pr,
            rawWert: p.value,
          });
        } else if (p.type === "labyrinthErreursRaw") {
          const raw = parseInt(p.value, 10);
          if (isNaN(raw)) continue;
          const pc = raw >= 7 ? "< 5" : "> 5";
          resultData.push({
            name: p.name,
            label: lang === "de" ? "Fehler PC" : "Erreurs PC",
            value: pc,
            rawErreurs: p.value,
          });
        } else if (p.type === "stroopVRaw") {
          if (!stroopV || stroopV.error) continue;
          const fmtPC = (c) => {
            const r = Math.round(c);
            if (r <= 0) return "< 1";
            if (r >= 100) return "> 99";
            return String(r);
          };
          if (p.kind === "tCouleurs" && stroopV.times?.couleurs?.centile != null) {
            resultData.push({ name: p.name, label: "Temps Couleurs PC", value: fmtPC(stroopV.times.couleurs.centile), rawTemps: p.value });
          } else if (p.kind === "tMots" && stroopV.times?.mots?.centile != null) {
            resultData.push({ name: p.name, label: "Temps Mots PC", value: fmtPC(stroopV.times.mots.centile), rawTemps: p.value });
          } else if (p.kind === "tInterference" && stroopV.times?.interference?.centile != null) {
            resultData.push({ name: p.name, label: "Temps Interférence PC", value: fmtPC(stroopV.times.interference.centile), rawTemps: p.value });
          } else if (p.kind === "ifMC" && stroopV.interferences?.ifMC?.centile != null) {
            resultData.push({ name: p.name, label: "Indice interférence faible PC", value: fmtPC(stroopV.interferences.ifMC.centile) });
          } else if (p.kind === "ifIC" && stroopV.interferences?.ifIC?.centile != null) {
            resultData.push({ name: p.name, label: "Indice d'interférence forte PC", value: fmtPC(stroopV.interferences.ifIC.centile) });
          } else if (p.kind === "errCouleurs" && stroopV.errors?.couleurs) {
            const e = stroopV.errors.couleurs;
            resultData.push({ name: p.name, label: "Erreur Couleurs PC", value: e.abnormal ? "< 5" : "> 5", rawErreurs: p.value, italic: true });
          } else if (p.kind === "errMots" && stroopV.errors?.mots) {
            const e = stroopV.errors.mots;
            resultData.push({ name: p.name, label: "Erreurs Mots PC", value: e.abnormal ? "< 5" : "> 5", rawErreurs: p.value, italic: true });
          } else if (p.kind === "errInterfTotal" && stroopV.errors?.interferenceTotal) {
            const e = stroopV.errors.interferenceTotal;
            resultData.push({ name: p.name, label: "Erreurs totales PC", value: e.isMin ? `> ${e.centile}` : String(e.centile), rawErreurs: p.value });
          } else if (p.kind === "errInterfNonCorr" && stroopV.errors?.interferenceNonCorr) {
            const e = stroopV.errors.interferenceNonCorr;
            resultData.push({ name: p.name, label: "Erreurs non-corrigées PC", value: e.isMin ? `> ${e.centile}` : String(e.centile), rawErreurs: p.value });
          } else if (p.kind === "errInterfCorr" && stroopV.errors?.interferenceCorr) {
            const e = stroopV.errors.interferenceCorr;
            resultData.push({ name: p.name, label: "Erreurs corrigées PC", value: e.isMin ? `> ${e.centile}` : String(e.centile), rawErreurs: p.value });
          }
        } else if (p.type === "input") {
          let chipLabel = null;
          if (p.value !== "" && (p.significatifThreshold != null || p.scoreLevels)) {
            const num = parseFloat(p.value);
            if (!isNaN(num)) {
              if (p.scoreLevels) {
                const matched = p.scoreLevels.find((lvl) => {
                  if (lvl.lt != null) return num < lvl.lt;
                  if (lvl.lte != null) return num <= lvl.lte;
                  return true;
                });
                chipLabel = matched?.label || null;
              } else {
                chipLabel = num >= p.significatifThreshold
                  ? (lang === "de" ? "signifikant" : "significatif")
                  : (lang === "de" ? "nicht signifikant" : "non significatif");
              }
            }
          } else if (p.value !== "" && testParams.evalRule === "dtla2017" && p.name === "score") {
            const num = parseFloat(p.value);
            if (!isNaN(num)) {
              const thr = getDtla2017Thresholds(age, patientInfo.anneesEtudes);
              chipLabel = getDtla2017Status(num, thr);
            }
          } else if (p.value !== "" && testParams.evalRule === "tgvScore" && p.name === "score") {
            chipLabel = getTGVPCLabel(p.value);
          }
          const stabilityParam = params.find((q) => q.type === "stability" && q.linkedTo === p.name);
          resultData.push({
            name: p.name,
            label: p.label,
            value: p.value || "",
            ...(chipLabel ? { chipLabel } : {}),
            ...(p.scoreMax != null ? { scoreMax: p.scoreMax } : {}),
            ...(p.italic ? { italic: true } : {}),
            ...(p.denom != null ? { kind: "brefScore", denom: p.denom, prefix: p.prefix || "" } : {}),
            ...(stabilityParam?.value ? { stability: stabilityParam.value } : {}),
          });
        } else if (p.type === "bellsRaw") {
          if (p.value === "" || p.value == null) continue;
          resultData.push({
            name: p.name,
            label: p.label,
            value: String(p.value),
            kind: "bellsRaw",
            bellsKind: p.kind,
          });
        } else if (p.type === "scoreClock") {
          resultData.push({
            name: p.name,
            label: p.label,
            value: p.value || "",
            kind: "scoreClock",
          });
        } else if (p.type === "deficitToggle" || p.type === "directionToggle") {
          resultData.push({
            name: p.name,
            label: p.label,
            value: p.value || "",
          });
        }
      }

      if (portesTotal) {
        resultData.push({
          name: "totalPC",
          label: lang === "de" ? "Gesamt PC" : "Total PC",
          value: portesTotal.pc,
          rawPortes: portesTotal.raw,
          kind: "portes",
        });
      }

      const brefSubItems = resultData.filter((r) => r.kind === "brefScore" && r.name !== "total");
      if (brefSubItems.length > 0 && brefSubItems.some((r) => r.value)) {
        const totalDenom = brefSubItems.reduce((s, r) => s + (r.denom || 0), 0);
        const totalValue = brefSubItems.reduce((s, r) => s + (parseFloat(r.value) || 0), 0);
        resultData.unshift({
          name: "total",
          label: "Total",
          kind: "brefScore",
          value: String(totalValue),
          denom: totalDenom,
          prefix: "",
        });
      }

      const resultText = (() => {
        // German Zahlenspanne: one line per condition combining the process-score
        // Wertpunkt (Prozesswert WP) and the longest-span percentile (Spanne PR).
        const deEmpanParams = params.filter((p) => p.type === "empanRaw" && empanResults[p.name]);
        if (lang === "de" && deEmpanParams.length > 0) {
          const COND_DE = { emcd: "Vorwärts", emci: "Rückwärts", emcc: "Sequentiell" };
          const STAB_DE = { stable: "stabil", instable: "instabil" };
          const deLines = [];
          for (const ep of deEmpanParams) {
            const pcStr = formatPCRange(empanResults[ep.name].pc);
            if (pcStr == null) continue;
            const subParam = params.find((q) => q.type === "subtestRaw" && q.kind === ep.kind.replace(/^e/, ""));
            const sub = subParam ? subtestResults[subParam.name] : null;
            const stabRaw = params.find((q) => q.type === "stability" && q.linkedTo === ep.name)?.value;
            const stab = STAB_DE[stabRaw] || stabRaw || "";
            const parts = [];
            if (sub && sub.ns != null) parts.push(`Prozesswert WP= ${sub.ns}`);
            parts.push(`Spanne PR= ${pcStr}`);
            const meta = [ep.value ? `${ep.value} Items` : null, stab || null].filter(Boolean).join(", ");
            deLines.push(`${COND_DE[ep.kind] || ep.label}: ${parts.join(", ")}${meta ? ` (${meta})` : ""}`);
          }
          if (deLines.length > 0) return deLines.join("\n");
        }

        const lines = [];
        const brefTotalItems = [];
        const brefSubFormatted = [];
        const portesParts = [];
        const fmtBref = (r) => `${r.prefix ? r.prefix + " " : ""}${r.value}/${r.denom}`;
        for (const r of resultData) {
          if (!r.value) continue;
          if (r.kind === "bellsRaw") continue;
          if (r.kind === "brefScore") {
            if (r.name === "total") brefTotalItems.push(fmtBref(r));
            else brefSubFormatted.push(fmtBref(r));
            continue;
          }
          if (r.kind === "portes") {
            const labelNoPC = (r.label || "").replace(/ PC$/, "");
            const v = String(r.value).trim();
            const pcText = v.startsWith(">") || v.startsWith("<") ? `PC${v.charAt(0)} ${v.replace(/^[<>]\s*/, "")}` : `PC= ${v}`;
            portesParts.push(`${labelNoPC} ${pcText}`);
            continue;
          }
          if (r.name === "commentaire" || ["comment", "kommentar"].includes((r.label || "").toLowerCase())) {
            lines.push(r.value);
            continue;
          }
          if (r.rawEmpan != null) {
            const stab = r.stability ? ` ${r.stability}` : "";
            lines.push(`${r.label.replace(/ PC$/, "")}: ${r.rawEmpan}${stab}, PC= ${r.value}`);
            continue;
          }
          if (r.rawWmsrGlobal != null) {
            lines.push(`${r.label.replace(/ PC$/, "")}: ${r.rawWmsrGlobal}, PC= ${r.value}`);
            continue;
          }
          if (r.rawTemps != null && r.rawTemps !== "") {
            lines.push(formatTimeLine(r.label, r.rawTemps, r.value));
            continue;
          }
          if (r.scoreMax != null || r.chipLabel || r.label === "Score") {
            const num = r.scoreMax != null ? `${r.value}/${r.scoreMax}` : `${r.value}`;
            if (r.chipLabel) {
              let sheetLabel;
              if (r.chipLabel.startsWith("score ") || r.chipLabel.startsWith("PC")) {
                sheetLabel = r.chipLabel;
              } else if (lang === "fr") {
                sheetLabel = `score ${r.chipLabel.replace("non significatif", "non-significatif")}`;
              } else {
                sheetLabel = r.chipLabel;
              }
              lines.push(`${num}, ${sheetLabel}`);
            } else {
              lines.push(num);
            }
            continue;
          }
          if (r.chipLabel) {
            lines.push(`${r.label}= ${r.value} — ${r.chipLabel}`);
            continue;
          }
          if (r.stability && r.rawEmpan == null) {
            const STAB_TXT = lang === "de"
              ? { stable: "stabil", instable: "instabil" }
              : { stable: "stable", instable: "instable" };
            const stab = STAB_TXT[r.stability] || r.stability;
            lines.push(`${r.label}= ${r.value} (${stab})`);
            continue;
          }
          lines.push(`${r.label}= ${r.value}`);
        }
        if (brefSubFormatted.length) lines.unshift(brefSubFormatted.join("; "));
        if (brefTotalItems.length) lines.unshift(brefTotalItems.join("; "));
        if (portesParts.length) lines.push(portesParts.join(", "));
        return lines.join("\n");
      })();
      if (gen !== evalGenerationRef.current) return;

      const expectedNorm = name.includes("WAIS-IV") ? "WAIS-IV" : name.includes("WAIS-III") ? "WAIS-III" : null;
      const actualNorm = age == null ? null : age >= 80 ? "WAIS-III" : "WAIS-IV";
      if (expectedNorm && actualNorm && expectedNorm !== actualNorm) {
        const altTest = expectedNorm === "WAIS-IV" ? name.replace("WAIS-IV", "WAIS-III") : name.replace("WAIS-III", "WAIS-IV");
        setWarning(
          lang === "de"
            ? `Alter ${age} Jahre: ${actualNorm}-Normen angewendet (besser « ${altTest} » verwenden)`
            : `Âge ${age} ans : normes ${actualNorm} appliquées (utilisez plutôt « ${altTest} »)`
        );
      } else {
        setWarning(null);
      }

      const evalResultData = [...resultData];
      if (testParams.evalRule === "bellsRousseaux" && bellsResult) {
        evalResultData.push({ name: "_bellsPath", label: "_bellsPath", value: bellsResult.pathological ? "1" : "0" });
      }
      const activeNorm = resolveActiveNorm();
      const evaluation = computeEvaluation(evalResultData, resultLoc, { evalRule: activeNorm?.evalRule ?? testParams.evalRule, evalCutoff: activeNorm?.evalCutoff ?? testParams.evalCutoff, bbepSubtest: activeNorm?.bbepSubtest ?? testParams.bbepSubtest, age, anneesEtudes: patientInfo.anneesEtudes, lang });

      let finalResultText = resultText;
      if (testParams.evalRule === "bellsRousseaux" && bellsResult) {
        const { cibles, omG, omD, temps, erreurs } = bellsResult;
        const isDe = lang === "de";
        const hasOm = omG != null && omD != null;
        const segments = [];
        if (hasOm) {
          segments.push(isDe
            ? `${cibles}/35 (${omG} Ausl. L, ${omD} Ausl. R)`
            : `${cibles}/35 (${omG} om. G, ${omD} om. D)`);
        }
        if (temps != null) {
          const tLabel = isDe ? (hasOm ? "Zeit" : "Zeit") : (hasOm ? "temps" : "Temps");
          segments.push(`${tLabel} ${fmtSeconds(temps)}`);
        }
        if (erreurs != null && erreurs > 0) {
          segments.push(isDe ? `${erreurs} Sel.-fehler` : `${erreurs} err. sél.`);
        }
        if (segments.length > 0) finalResultText = segments.join(", ");
      }
      if (testParams.evalRule === "bissectionFerber") {
        const dev = params.find((p) => p.name === "deviation")?.value;
        const dir = params.find((p) => p.name === "direction")?.value;
        if (dev && dir) {
          const isDe = lang === "de";
          finalResultText = isDe
            ? `Mittlere Abweichung ${dev}% nach ${dir} (Cut-off >5%)`
            : `Déviation moy. ${dev}% vers la ${dir} (Cut-off >5%)`;
        }
      }
      if (testParams.evalRule === "vosp") {
        const VOSP_MAX = {
          screening: 20,
          lettresFragmentees: 20,
          denombrementPoints: 10,
          discriminationPosition: 20,
          localisationChiffres: 10,
          analysesCubes: 10,
        };
        const lines = [];
        for (const p of params) {
          const max = VOSP_MAX[p.name];
          if (max == null || p.value === "" || p.value == null) continue;
          lines.push(`${String(p.label).replace(/\s*\/\d+$/, "")}: ${p.value}/${max}`);
        }
        if (lines.length > 0) finalResultText = lines.join("\n");
      }
      if (testParams.evalRule === "applesHumphreys") {
        const isDe = lang === "de";
        const co = isDe ? "Cut-off" : "cut-off";
        const num = (n) => {
          const p = params.find((x) => x.name === n);
          const raw = p?.value ?? "";
          return { raw, n: parseFloat(String(raw).replace(",", ".")) };
        };
        const cibles = num("cibles");
        const diff = num("diffGD");
        const asym = num("asymetrie");
        const note = params.find((x) => x.name === "commentaire")?.value || "";
        const ciblesLbl = isDe ? "Erkannte Ziele" : "Cibles détectées";
        const diffLbl = isDe ? "Diff. Auslassungen L-R" : "Diff. omissions G-D";
        const asymLbl = isDe ? "Asymmetrie Erkennung inkompletter Äpfel" : "asymétrie détection de pommes incomplètes";
        const ciblesSeg = `${ciblesLbl} ${cibles.raw !== "" ? cibles.raw : "X"}/50 ${Number.isFinite(cibles.n) && cibles.n <= 42 ? `(< ${co} 42)` : `(> ${co} 42)`}`;
        const diffSeg = `${diffLbl} ${diff.raw !== "" ? diff.raw : "X"} ${Number.isFinite(diff.n) && diff.n > 2 ? `> ${co} (> 2)` : `(${co} > 2)`}`;
        const asymSeg = `${asymLbl} ${asym.raw !== "" ? asym.raw : "X"} ${Number.isFinite(asym.n) && asym.n > 1 ? `> ${co} (> 1)` : `(${co} > 1)`}`;
        const p1 = `${ciblesSeg}, ${diffSeg}, ${asymSeg}`;
        finalResultText = note ? `${p1}\n\n${note}` : p1;
      }

      updateTest(name, { resultData, resultText: finalResultText, evaluation, empanResults, subtestResults, labyrinthResults, wmsrBlockspanResults, stroopDkefsResults, tmtAErreursResults, tmtBPersevResults, bildertestResults, stroopV, bellsResult, portesResults, portesTotal });
    } catch (e) {
      console.error("handleManualEval error:", e);
    }
  };

  const paramsJson = JSON.stringify(testParams?.params?.map((p) => p.value));
  const { dateNaissance: _dn, dateExamen: _de } = patientInfo;
  useEffect(() => {
    if (testParams.type !== "manual") return;
    const hasValue = testParams?.params?.some(
      (p) => (p.type === "input" || p.type === "empanRaw" || p.type === "subtestRaw" || p.type === "labyrinthTempsRaw" || p.type === "labyrinthErreursRaw" || p.type === "scoreClock" || p.type === "stroopVRaw" || p.type === "stroopDkefsRaw" || p.type === "tmtAErreursRaw" || p.type === "tmtBPersevRaw" || p.type === "deficitToggle" || p.type === "directionToggle" || p.type === "bildertestWertRaw" || p.type === "bildertestSettingToggle" || p.type === "amievaRaw" || p.type === "vdlRaw" || p.type === "bellsRaw" || p.type === "portesRaw" || p.type === "wmsrBlockspanRaw") && p.value
    );
    if (!hasValue) return;
    if (autoCalcTimer.current) clearTimeout(autoCalcTimer.current);
    autoCalcTimer.current = setTimeout(() => {
      handleManualEval();
    }, 400);
    return () => {
      if (autoCalcTimer.current) clearTimeout(autoCalcTimer.current);
    };
  }, [
    paramsJson,
    _dn,
    _de,
    patientInfo.niveau,
    patientInfo.genre,
    patientInfo.anneesEtudes,
    stroopVA_tempsPC,
    stroop1_tempsNS,
    stroop3_tempsNS,
    ceradAbrufIntrusions,
    ceradLernTrial3,
    ceradFigAbzTotal,
    ceradTrailATime,
  ]);

  // Auto-calculate for parquet tests when required fields are filled
  const temps = getParamValue("temps");
  const erreurs = getParamValue("erreurs");
  const rawScore = getParamValue("rawScore");
  const copyRaw = getParamValue("copyRaw");
  const timeRaw = getParamValue("timeRaw");
  const immediateRaw = getParamValue("immediateRaw");
  const delayedRaw = getParamValue("delayedRaw");
  const { dateNaissance, dateExamen, anneesEtudes } = patientInfo;

  useEffect(() => {
    if (testParams.type !== "parquet") return;

    // Check minimum required fields per test type
    if (testParams.trailNumber) {
      if (!temps) return;
    } else if (["waisCode", "waisCodeIII", "waisSym", "waisSymIII"].includes(testParams.calcType)) {
      if (!rawScore) return;
    } else if (["lgI", "lgII", "lgIIRecog"].includes(testParams.calcType)) {
      if (rawScore === "" || rawScore == null) return;
    } else if (testParams.calcType === "rcftCopie") {
      if (!copyRaw && !timeRaw) return;
    } else if (testParams.calcType === "rcftImmediat") {
      if (!immediateRaw) return;
    } else if (testParams.calcType === "rcftDiffere") {
      if (!delayedRaw) return;
    } else {
      return;
    }

    if (autoCalcTimer.current) clearTimeout(autoCalcTimer.current);
    autoCalcTimer.current = setTimeout(() => {
      handleCalculer();
    }, 400);

    return () => {
      if (autoCalcTimer.current) clearTimeout(autoCalcTimer.current);
    };
  }, [temps, erreurs, rawScore, copyRaw, timeRaw, immediateRaw, delayedRaw, dateNaissance, dateExamen, anneesEtudes, ct1TempsLive]);

  // Clear stored results when versionByAge no longer matches the current age,
  // so we don't keep writing stale resultText/evaluation into the scoresheet
  // for a test whose card has been hidden.
  const ageForVersion = calcAge(dateNaissance, dateExamen);
  const versionOutOfBand = testParams?.versionByAge != null
    && ageForVersion !== null
    && !resolveActiveVersion();
  useEffect(() => {
    if (!versionOutOfBand) return;
    const hasStored = test?.resultData != null || test?.resultText != null || test?.evaluation != null;
    if (hasStored) {
      updateTest(name, { resultData: null, resultText: null, evaluation: null });
    }
  }, [versionOutOfBand]);

  if (!testParams) return null;

  // Hide tests whose versionByAge config has no entry matching the current age
  // (e.g. WMS-IV LG for age < 16 or > 90). When age is unknown we still render.
  if (versionOutOfBand) return null;

  const activeVersion = resolveActiveVersion();

  return (
    <Box
      mt={2}
      {...(dropKind && {
        onDragEnter: handleFileDragEnter,
        onDragOver: handleFileDragOver,
        onDragLeave: handleFileDragLeave,
        onDrop: handleDropFile,
      })}
    >
      <SecondaryColoredBorderCard
        borderWidth={2}
        sx={fileDragging ? { borderColor: "primary.main", backgroundColor: "action.hover" } : undefined}
      >
        <Stack m={2} spacing={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" color={"primary"}>
                {getTestLabel(name, rawTestParams, lang)}
              </Typography>
              {testParams.ageThresholds && (
                <Typography variant="caption" color="textSecondary">
                  {resolveResultLoc() === testParams.ageThresholds[0].resultLoc
                    ? testParams.ageThresholds[0].label
                    : testParams.ageThresholds[testParams.ageThresholds.length - 1].label}
                </Typography>
              )}
              {activeVersion && (
                <Typography variant="caption" color="textSecondary" display="block">
                  {resolveLabel(activeVersion.label, lang)}
                </Typography>
              )}
            </Box>
            <Box>
              <Tooltip title={t("test.reset")}>
                <IconButton
                  size="small"
                  onClick={() => setResetDialogOpen(true)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => setCommentDialogOpen(true)}
                color={test?.comment ? "primary" : "default"}
              >
                <Badge variant="dot" invisible={!test?.comment} color="primary">
                  <ChatBubbleOutlineIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Box>
          </Box>
          {testParams.type === "rtf" && (
            <Stack spacing={1}>
              <Box sx={dropZoneSx}>
                <Button
                  variant={"outlined"}
                  color={"secondary"}
                  onClick={() => setRtfDialogOpen(true)}
                  disabled={fileLoading}
                >
                  {t("test.upload")}
                </Button>
                {dropHint("RTF")}
              </Box>
              {test?.resultData?.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
                  {test.resultData.map((r, i) => (
                    <Box key={i} sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                      <Typography variant="body2" color="primary" >
                        {r.label} = {r.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {testParams.params?.filter((p) => p.type === "significatifToggle").map((param) => (
                <Stack key={param.name} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Typography variant="body2">{param.label}:</Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={param.value || null}
                    onChange={(_e, val) => handleParamChange(param.name, val || "")}
                  >
                    <ToggleButton value="significative">{t("test.significative")}</ToggleButton>
                    <ToggleButton value="pas significative">{t("test.pasSignificative")}</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              ))}
              <TextField
                placeholder={t("test.clinicalNote")}
                fullWidth
                multiline
                size="small"
                value={test?.comment || ""}
                onChange={(e) => updateTest(name, { comment: e.target.value })}
                sx={{ mt: 1 }}
              />
            </Stack>
          )}
          {testParams.type === "nosger" && (
            <Stack spacing={1}>
              <Box sx={dropZoneSx}>
                <Button
                  variant={"outlined"}
                  color={"secondary"}
                  onClick={handleUploadNosger}
                  disabled={fileLoading}
                  size="small"
                >
                  {test?.nosgerFile ? `NOSGER XLS (${test.nosgerFile})` : "NOSGER XLS"}
                </Button>
                {dropHint("XLS")}
              </Box>
              {test?.resultData?.length > 0 && (
                <Stack spacing={0.5}>
                  {test.resultData.map((r) => (
                    <Box key={r.name} sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main", alignSelf: "flex-start" }}>
                      <Typography variant="body2" color="primary">
                        {r.label}: {r.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
          {testParams.type === "bdq" && (
            <Stack spacing={1}>
              <Box sx={dropZoneSx}>
                <Button
                  variant={"outlined"}
                  color={"secondary"}
                  onClick={handleUploadBdq}
                  disabled={fileLoading}
                  size="small"
                >
                  {test?.bdqFile ? `BDQ XLS (${test.bdqFile})` : "BDQ XLS"}
                </Button>
                {dropHint("XLS")}
              </Box>
              {test?.resultData?.length > 0 && (
                <Stack spacing={0.5}>
                  {test.resultData.map((r) => (
                    <Box key={r.name} sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main", alignSelf: "flex-start" }}>
                      <Typography variant="body2" color="primary">
                        {r.label}: {r.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
          {testParams.type === "manual" && testParams.params?.some((p) => p.type === "input" || p.type === "empanRaw" || p.type === "subtestRaw" || p.type === "labyrinthTempsRaw" || p.type === "labyrinthErreursRaw" || p.type === "scoreClock" || p.type === "stroopVRaw" || p.type === "stroopDkefsRaw" || p.type === "tmtAErreursRaw" || p.type === "tmtBPersevRaw" || p.type === "deficitToggle" || p.type === "directionToggle" || p.type === "bildertestWertRaw" || p.type === "bildertestSettingToggle" || p.type === "amievaRaw" || p.type === "vdlRaw" || p.type === "bellsRaw" || p.type === "portesRaw" || p.type === "wmsrBlockspanRaw") && (
            <Stack spacing={2}>
              {testParams.geria12Cells && (
                <Box sx={{ ...dropZoneSx, mb: 1 }}>
                  <Button size="small" variant="outlined" color="secondary" onClick={handleUploadGeria12} disabled={fileLoading}>
                    {test?.geria12File ? `GERIA-12 XLS (${test.geria12File})` : "GERIA-12 XLS"}
                  </Button>
                  {dropHint("XLS")}
                </Box>
              )}
              <Stack spacing={1.5}>
                {(() => {
                  const visible = testParams.params.filter((p) => {
                    if (p.name === "commentaire") return false;
                    if (p.type === "stability") {
                      const target = testParams.params.find((q) => q.name === p.linkedTo);
                      return target?.type === "input";
                    }
                    return p.type === "input" || p.type === "empanRaw" || p.type === "subtestRaw" || p.type === "labyrinthTempsRaw" || p.type === "labyrinthErreursRaw" || p.type === "scoreClock" || p.type === "stroopVRaw" || p.type === "stroopDkefsRaw" || p.type === "tmtAErreursRaw" || p.type === "tmtBPersevRaw" || p.type === "deficitToggle" || p.type === "directionToggle" || p.type === "bildertestWertRaw" || p.type === "bildertestSettingToggle" || p.type === "amievaRaw" || p.type === "vdlRaw" || p.type === "bellsRaw" || p.type === "portesRaw" || p.type === "wmsrBlockspanRaw";
                  });
                  const rows = [];
                  const consumed = new Set();
                  const isLabyrinth = (p) => p.type === "labyrinthTempsRaw" || p.type === "labyrinthErreursRaw";
                  // Splits letter+digit name runs (r1,r2,r3,i1,i2,i3) into
                  // per-prefix rows so wrapping is 3+3 instead of an
                  // arbitrary width-dependent break (e.g. orphaning the last
                  // field alone on CERAD's 10-mots card). Names without a
                  // digit suffix (kreis, sim, item1..4) are unaffected.
                  const digitPrefix = (nm) => (/^([a-zA-Z]+)\d+$/.exec(nm) || [])[1] || null;
                  for (let i = 0; i < visible.length; i++) {
                    const p = visible[i];
                    if (consumed.has(p.name)) continue;
                    if (p.type === "empanRaw") {
                      const group = [p];
                      for (let j = i + 1; j < visible.length; j++) {
                        if (visible[j].type === "subtestRaw") {
                          group.push(visible[j]);
                          consumed.add(visible[j].name);
                          break;
                        }
                        if (visible[j].type === "empanRaw") break;
                      }
                      rows.push(group);
                    } else if (isLabyrinth(p)) {
                      const group = [p];
                      for (let j = i + 1; j < visible.length; j++) {
                        if (!isLabyrinth(visible[j])) break;
                        group.push(visible[j]);
                        consumed.add(visible[j].name);
                      }
                      rows.push(group);
                    } else if (p.type === "stroopVRaw") {
                      const group = [p];
                      for (let j = i + 1; j < visible.length; j++) {
                        if (visible[j].type !== "stroopVRaw") break;
                        group.push(visible[j]);
                        consumed.add(visible[j].name);
                      }
                      rows.push(group);
                    } else if (p.type === "stroopDkefsRaw") {
                      const group = [p];
                      for (let j = i + 1; j < visible.length; j++) {
                        if (visible[j].type !== "stroopDkefsRaw" && visible[j].type !== "input") break;
                        group.push(visible[j]);
                        consumed.add(visible[j].name);
                      }
                      rows.push(group);
                    } else if (p.type === "amievaRaw") {
                      const group = [p];
                      for (let j = i + 1; j < visible.length; j++) {
                        if (visible[j].type !== "amievaRaw") break;
                        group.push(visible[j]);
                        consumed.add(visible[j].name);
                      }
                      rows.push(group);
                    } else if (p.type === "vdlRaw") {
                      const group = [p];
                      for (let j = i + 1; j < visible.length; j++) {
                        if (visible[j].type !== "vdlRaw") break;
                        group.push(visible[j]);
                        consumed.add(visible[j].name);
                      }
                      rows.push(group);
                    } else if (p.type === "bellsRaw") {
                      const group = [p];
                      for (let j = i + 1; j < visible.length; j++) {
                        if (visible[j].type !== "bellsRaw") break;
                        group.push(visible[j]);
                        consumed.add(visible[j].name);
                      }
                      rows.push(group);
                    } else if (p.type === "bildertestSettingToggle") {
                      const group = [p];
                      for (let j = i + 1; j < visible.length; j++) {
                        if (visible[j].type !== "bildertestWertRaw") break;
                        group.push(visible[j]);
                        consumed.add(visible[j].name);
                      }
                      rows.push(group);
                    } else if (p.type === "input" && visible[i + 1]?.type === "directionToggle") {
                      const group = [p, visible[i + 1]];
                      consumed.add(visible[i + 1].name);
                      rows.push(group);
                    } else if (p.type === "input" && visible[i + 1]?.type === "stability" && visible[i + 1]?.linkedTo === p.name) {
                      const group = [p, visible[i + 1]];
                      consumed.add(visible[i + 1].name);
                      rows.push(group);
                    } else if (p.type === "input" && (name.includes("Stroop") || name.includes("GERIA-12") || name.includes("RL/RI") || name.includes("CERAD") || p.denom != null)) {
                      const group = [p];
                      const groupPrefix = digitPrefix(p.name);
                      for (let j = i + 1; j < visible.length; j++) {
                        if (visible[j].type !== "input") break;
                        const nextPrefix = digitPrefix(visible[j].name);
                        if (groupPrefix != null && nextPrefix != null && nextPrefix !== groupPrefix) break;
                        group.push(visible[j]);
                        consumed.add(visible[j].name);
                      }
                      rows.push(group);
                    } else {
                      rows.push([p]);
                    }
                  }

                  const renderParam = (param) => {
                    if (param.type === "stroopDkefsRaw") {
                      const r = test?.stroopDkefsResults?.[param.name];
                      const isDiff = param.kind === "cond31Diff" || param.kind === "cond43Diff";
                      const isTime = r?.ns != null;
                      const isZeroErrors = !isTime && parseInt(param.value, 10) === 0;

                      const chipText = isTime
                        ? (isDiff
                            ? [
                                r.diff != null ? `ΔNS = ${r.diff}` : null,
                                `NS = ${r.ns}`,
                              ].filter(Boolean).join(" → ")
                            : `NS = ${r.ns}`)
                        : (r?.pc != null ? (isZeroErrors ? "PC > 10" : `PC = ${r.pc}`) : null);
                      const showError = !!r?.error && (isDiff || param.value);
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center" sx={isDiff ? { height: 40 } : undefined}>
                          {isDiff ? (
                            <Typography variant="body2" color="textSecondary">
                              {param.label.replace(/\s*NS\s*$/, "")}
                            </Typography>
                          ) : (
                            <TextField
                              label={param.label}
                              size="small"
                              value={param.value || ""}
                              onChange={(e) => handleParamChange(param.name, e.target.value)}
                              sx={{ width: 130 }}
                            />
                          )}
                          {chipText && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                              <Typography variant="body2" color="primary">
                                {chipText}
                              </Typography>
                            </Box>
                          )}
                          {showError && (
                            <Typography variant="caption" color="warning.main">
                              {r.error}
                            </Typography>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "stroopVRaw") {
                      const entry = test?.resultData?.find((r) => r.name === param.name);
                      const isDisplayOnly = param.kind === "ifMC" || param.kind === "ifIC";
                      const chipText = entry?.value
                        ? (entry.label.includes("PC")
                            ? (/^[<>]/.test(entry.value) ? `PC ${entry.value}` : `PC = ${entry.value}`)
                            : entry.value)
                        : null;

                      let depError = null;
                      if (isDisplayOnly && !chipText) {
                        const ownKind = param.kind === "ifMC" ? "tMots" : "tInterference";
                        const ownLabel = param.kind === "ifMC"
                          ? (lang === "de" ? "Zeit Wörter" : "Temps Mots")
                          : (lang === "de" ? "Zeit Interferenz" : "Temps Interférence");
                        const ownVal = testParams.params.find((q) => q.kind === ownKind)?.value;
                        const otherVal = stroopVA_tempsPC;
                        const missing = [];
                        if (!ownVal) missing.push(ownLabel);
                        if (!otherVal) missing.push(lang === "de" ? "Zeit Farben (Teil A)" : "Temps Couleurs (partie A)");
                        if (missing.length > 0) {
                          depError = lang === "de"
                            ? `${missing.join(" und ")} angeben`
                            : `Renseignez ${missing.join(" et ")}`;
                        }
                      }

                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          {isDisplayOnly ? (
                            <Typography variant="body2" color="textSecondary" sx={{ minWidth: 80 }}>
                              {param.label}
                            </Typography>
                          ) : (
                            <TextField
                              label={param.label}
                              size="small"
                              value={param.value || ""}
                              onChange={(e) => handleParamChange(param.name, e.target.value)}
                              sx={{ width: 160 }}
                            />
                          )}
                          {chipText && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                              <Typography variant="body2" color="primary">
                                {chipText}
                              </Typography>
                            </Box>
                          )}
                          {depError && (
                            <Typography variant="caption" color="warning.main">
                              {depError}
                            </Typography>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "portesRaw") {
                      const r = test?.portesResults?.[param.name];
                      const chipText = r?.pc ? formatPortesPC(r.pc) : null;
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={param.label}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ width: 140 }}
                          />
                          {chipText && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                              <Typography variant="body2" color="primary">
                                {chipText}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "tmtAErreursRaw") {
                      const r = test?.tmtAErreursResults?.[param.name];
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={param.label}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ width: 100 }}
                          />
                          {r?.pc && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                              <Typography variant="body2" color="primary">
                                {/^[<>]/.test(r.pc) ? `PC ${r.pc}` : `PC = ${r.pc}`}
                              </Typography>
                            </Box>
                          )}
                          {r?.error && (
                            <Typography variant="caption" color="warning.main">
                              {r.error}
                            </Typography>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "tmtBPersevRaw") {
                      const r = test?.tmtBPersevResults?.[param.name];
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={param.label}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ width: 100 }}
                          />
                          {r?.pc && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                              <Typography variant="body2" color="primary">
                                {/^[<>]/.test(r.pc) ? `PC ${r.pc}` : `PC = ${r.pc}`}
                              </Typography>
                            </Box>
                          )}
                          {r?.error && (
                            <Typography variant="caption" color="warning.main">
                              {r.error}
                            </Typography>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "labyrinthErreursRaw") {
                      const entry = test?.resultData?.find((r) => r.name === param.name);
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={param.label}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ width: 100 }}
                          />
                          {entry?.value && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                              <Typography variant="body2" color="primary">
                                PC = {entry.value}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "wmsrBlockspanRaw") {
                      const r = test?.wmsrBlockspanResults?.[param.name];
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={param.label}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ width: 150 }}
                          />
                          {r?.pc != null && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                              <Typography variant="body2" color="primary">
                                PC = {r.pc}
                              </Typography>
                            </Box>
                          )}
                          {r?.error && (
                            <Typography variant="caption" color="error">
                              {r.error}
                            </Typography>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "labyrinthTempsRaw") {
                      const r = test?.labyrinthResults?.[param.name];
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={param.label}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ width: 120 }}
                          />
                          {r?.pr && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                              <Typography variant="body2" color="primary">
                                PC = {r.pr}
                              </Typography>
                            </Box>
                          )}
                          {r?.error && (
                            <Typography variant="caption" color="error">
                              {r.error}
                            </Typography>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "subtestRaw") {
                      const sub = test?.subtestResults?.[param.name];
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={param.label}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ width: 150 }}
                          />
                          {sub?.ns != null && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                              <Typography variant="body2" color="primary">
                                {param.nsLabel || "NS"} = {sub.ns}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "empanRaw") {
                      const stabilityParam = testParams.params.find(
                        (q) => q.type === "stability" && q.linkedTo === param.name
                      );
                      const empanRes = test?.empanResults?.[param.name];
                      const pcStr = empanRes?.pc ? formatPCRange(empanRes.pc) : null;
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={param.label}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ width: 180 }}
                          />
                          {stabilityParam && (
                            <ToggleButtonGroup
                              size="small"
                              exclusive
                              value={stabilityParam.value || null}
                              onChange={(_e, val) => handleParamChange(stabilityParam.name, val || "")}
                            >
                              <ToggleButton value="stable">{lang === "de" ? "stabil" : "stable"}</ToggleButton>
                              <ToggleButton value="instable">{lang === "de" ? "instabil" : "instable"}</ToggleButton>
                            </ToggleButtonGroup>
                          )}
                          {pcStr && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                              <Typography variant="body2" color="primary">
                                PC = {pcStr}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "bildertestSettingToggle") {
                      return (
                        <ToggleButtonGroup
                          key={param.name}
                          size="small"
                          exclusive
                          value={param.value || "haushalt"}
                          onChange={(_e, val) => { if (val) handleParamChange(param.name, val); }}
                        >
                          <ToggleButton value="haushalt">{t("test.indep")}</ToggleButton>
                          <ToggleButton value="heim">{t("test.ems")}</ToggleButton>
                        </ToggleButtonGroup>
                      );
                    }
                    if (param.type === "bildertestWertRaw") {
                      const r = test?.bildertestResults?.[param.name];
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={param.label}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ width: 140 }}
                          />
                          {r?.pr && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                              <Typography variant="body2" color="primary">
                                {/^[<>]/.test(r.pr) ? `PC ${r.pr}` : `PC = ${r.pr}`}
                              </Typography>
                            </Box>
                          )}
                          {r?.error && (
                            <Typography variant="caption" color="warning.main">
                              {r.error}
                            </Typography>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "deficitToggle") {
                      return (
                        <ToggleButtonGroup
                          key={param.name}
                          size="small"
                          exclusive
                          value={param.value || null}
                          onChange={(_e, val) => handleParamChange(param.name, val || "")}
                        >
                          <ToggleButton value="déficitaire">{t("test.deficit")}</ToggleButton>
                          <ToggleButton value="non déficitaire">{t("test.nonDeficit")}</ToggleButton>
                        </ToggleButtonGroup>
                      );
                    }
                    if (param.type === "directionToggle") {
                      return (
                        <ToggleButtonGroup
                          key={param.name}
                          size="small"
                          exclusive
                          value={param.value || null}
                          onChange={(_e, val) => handleParamChange(param.name, val || "")}
                        >
                          <ToggleButton value="D">D</ToggleButton>
                          <ToggleButton value="G">G</ToggleButton>
                        </ToggleButtonGroup>
                      );
                    }
                    if (param.type === "stability") {
                      return (
                        <ToggleButtonGroup
                          key={param.name}
                          size="small"
                          exclusive
                          value={param.value || null}
                          onChange={(_e, val) => handleParamChange(param.name, val || "")}
                        >
                          <ToggleButton value="stable">{lang === "de" ? "stabil" : "stable"}</ToggleButton>
                          <ToggleButton value="instable">{lang === "de" ? "instabil" : "instable"}</ToggleButton>
                        </ToggleButtonGroup>
                      );
                    }
                    if (param.type === "amievaRaw") {
                      const r = test?.amievaResults?.[param.name];
                      const pcStr = r?.centile ? centileToPCStr(r.centile) : null;
                      const chipText = pcStr ? (/^[<>]/.test(pcStr) ? `PC ${pcStr}` : `PC = ${pcStr}`) : null;
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={param.label}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ width: 240 }}
                          />
                          {chipText && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main", whiteSpace: "nowrap" }}>
                              <Typography variant="body2" color="primary">
                                {chipText}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "vdlRaw") {
                      const r = test?.vdlResults?.[param.name];
                      let pcStr = null;
                      if (r?.percentile != null) pcStr = vdlPercentileToPCStr(r.percentile);
                      else if (r?.centile) pcStr = vdlCentileToPCStr(r.centile);
                      const chipText = pcStr ? (/^[<>]/.test(pcStr) ? `PC ${pcStr}` : `PC = ${pcStr}`) : null;
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={param.label}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ width: 90 }}
                          />
                          {chipText && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main", whiteSpace: "nowrap" }}>
                              <Typography variant="body2" color="primary">
                                {chipText}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "bellsRaw") {
                      const r = test?.bellsResult;
                      let chipText = null;
                      let chipPath = false;
                      if (r) {
                        if (param.kind === "omG" || param.kind === "omD") {
                          if (r.omTotal != null) {
                            const cutoff = r.cutoffs?.omissions;
                            const cutoffStr = cutoff != null ? ` (>${cutoff})` : "";
                            chipText = `Total = ${r.omTotal}${cutoffStr}`;
                            chipPath = !!(r.flags?.omPath || r.flags?.diffPath);
                          }
                        } else if (param.kind === "erreurs") {
                          if (r.erreurs != null) {
                            chipText = r.erreurs >= 1 ? "≥ 1 (path.)" : "OK";
                            chipPath = !!r.flags?.errPath;
                          }
                        } else if (param.kind === "temps") {
                          if (r.temps != null && r.cutoffs?.temps != null) {
                            chipText = `Cut-off > ${fmtSeconds(r.cutoffs.temps)}`;
                            chipPath = !!r.flags?.tempsPath;
                          }
                        }
                      }
                      const borderColor = chipPath ? "error.main" : "primary.main";
                      const textColor = chipPath ? "error.main" : "primary.main";
                      const inputWidth = param.kind === "temps" ? 120 : 130;
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={param.label}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ width: inputWidth }}
                          />
                          {chipText && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor, whiteSpace: "nowrap" }}>
                              <Typography variant="body2" sx={{ color: textColor }}>
                                {chipText}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      );
                    }
                    if (param.type === "scoreClock") {
                      return (
                        <TextField
                          key={param.name}
                          label={param.label}
                          placeholder={lang === "de" ? "z.B. 5/7" : "ex: 8/10"}
                          size="small"
                          value={param.value || ""}
                          onChange={(e) => handleParamChange(param.name, e.target.value)}
                          sx={{ width: 140 }}
                        />
                      );
                    }
                    if (param.significatifThreshold != null || param.scoreLevels || param.scoreMax != null) {
                      const num = parseFloat(param.value);
                      const showChip = !isNaN(num);
                      let chipLabel = "";
                      let dtlaMissing = null;
                      if (showChip) {
                        if (param.scoreLevels) {
                          const matched = param.scoreLevels.find((lvl) => {
                            if (lvl.lt != null) return num < lvl.lt;
                            if (lvl.lte != null) return num <= lvl.lte;
                            return true;
                          });
                          chipLabel = matched?.label || "";
                        } else if (param.significatifThreshold != null) {
                          chipLabel = num >= param.significatifThreshold
                            ? (lang === "de" ? "signifikant" : "significatif")
                            : (lang === "de" ? "nicht signifikant" : "non significatif");
                        } else if (testParams.evalRule === "dtla2017" && param.name === "score") {
                          const age = calcAge(patientInfo.dateNaissance, patientInfo.dateExamen);
                          const thr = getDtla2017Thresholds(age, patientInfo.anneesEtudes);
                          const status = getDtla2017Status(num, thr);
                          if (status) {
                            chipLabel = `${num}/${param.scoreMax} — ${status}`;
                          } else {
                            const missing = [];
                            if (age == null) missing.push(lang === "de" ? "Geburtsdatum" : "date de naissance");
                            if (!patientInfo.anneesEtudes) missing.push(lang === "de" ? "Ausbildungsjahre" : "années d'études");
                            if (missing.length === 0 && (age < 50 || age > 80)) {
                              missing.push(lang === "de" ? "Alter außerhalb der Normen (50–80)" : "âge hors normes (50–80)");
                            }
                            dtlaMissing = missing.length
                              ? (lang === "de" ? `${missing.join(" und ")} angeben` : `Renseignez ${missing.join(" et ")}`)
                              : null;
                          }
                        } else if (testParams.evalRule === "tgvScore" && param.name === "score") {
                          chipLabel = getTGVPCLabel(param.value) || "";
                        }
                      }
                      const fieldLabel = param.scoreMax != null ? `${param.label} (/${param.scoreMax})` : param.label;
                      return (
                        <Stack key={param.name} direction="row" spacing={1} alignItems="center">
                          <TextField
                            label={fieldLabel}
                            size="small"
                            value={param.value || ""}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                            sx={{ minWidth: 120 }}
                          />
                          {showChip && chipLabel && (
                            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                              <Typography variant="body2" color="primary">
                                {chipLabel}
                              </Typography>
                            </Box>
                          )}
                          {showChip && dtlaMissing && (
                            <Typography variant="caption" color="warning.main">
                              {dtlaMissing}
                            </Typography>
                          )}
                          {testParams.ageNorms && param.name === "score" && (() => {
                            const n = resolveActiveNorm();
                            return (
                              <Typography variant="caption" color={n ? "textSecondary" : "warning.main"}>
                                {n
                                  ? `${lang === "de" ? "Norm" : "Norme"} : ${dl(n.label)}`
                                  : lang === "de" ? "Geburtsdatum für Norm nötig" : "Date de naissance requise pour la norme"}
                              </Typography>
                            );
                          })()}
                        </Stack>
                      );
                    }
                    return (
                      <TextField
                        key={param.name}
                        label={param.label}
                        size="small"
                        value={param.value || ""}
                        onChange={(e) => handleParamChange(param.name, e.target.value)}
                        sx={param.denom != null ? { width: 140 } : { minWidth: 120 }}
                      />
                    );
                  };

                  return rows.map((group, idx) => {
                    if (group[0]?.type === "amievaRaw") {
                      // Same column layout as Van der Linden: free recall |
                      // cued recall | everything else.
                      const libreNames = ["rappelLibre1", "rappelLibre2", "rappelLibre3", "rappelLibreDiffere"];
                      const indiceNames = ["rappelIndice1", "rappelIndice2", "rappelIndice3", "rappelIndiceDiffere"];
                      const libre = libreNames.map((n) => group.find((p) => p.name === n)).filter(Boolean);
                      const indice = indiceNames.map((n) => group.find((p) => p.name === n)).filter(Boolean);
                      const rest = group.filter(
                        (p) => !libreNames.includes(p.name) && !indiceNames.includes(p.name)
                      );
                      return (
                        <Box
                          key={idx}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, max-content)",
                            columnGap: 4,
                            rowGap: 1.5,
                            alignItems: "center",
                          }}
                        >
                          {libre.map((p, i) => (
                            <Box key={p.name} sx={{ gridColumn: 1, gridRow: i + 1 }}>
                              {renderParam(p)}
                            </Box>
                          ))}
                          {indice.map((p, i) => (
                            <Box key={p.name} sx={{ gridColumn: 2, gridRow: i + 1 }}>
                              {renderParam(p)}
                            </Box>
                          ))}
                          {rest.map((p, i) => (
                            <Box key={p.name} sx={{ gridColumn: 3, gridRow: i + 1 }}>
                              {renderParam(p)}
                            </Box>
                          ))}
                        </Box>
                      );
                    }
                    if (group[0]?.type === "vdlRaw") {
                      const libreNames = new Set(["RL1", "RL2", "RL3", "RLD"]);
                      const totalNames = new Set(["RT1", "RT2", "RT3", "RTD"]);
                      const libre = group.filter((p) => libreNames.has(p.name));
                      const total = group.filter((p) => totalNames.has(p.name));
                      const rest = group.filter(
                        (p) => !libreNames.has(p.name) && !totalNames.has(p.name)
                      );
                      return (
                        <Box
                          key={idx}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, max-content)",
                            columnGap: 4,
                            rowGap: 1.5,
                            alignItems: "center",
                          }}
                        >
                          {libre.map((p, i) => (
                            <Box key={p.name} sx={{ gridColumn: 1, gridRow: i + 1 }}>
                              {renderParam(p)}
                            </Box>
                          ))}
                          {total.map((p, i) => (
                            <Box key={p.name} sx={{ gridColumn: 2, gridRow: i + 1 }}>
                              {renderParam(p)}
                            </Box>
                          ))}
                          {rest.map((p) => (
                            <Box key={p.name} sx={{ gridColumn: 3, gridRow: libre.length }}>
                              {renderParam(p)}
                            </Box>
                          ))}
                        </Box>
                      );
                    }
                    return (
                      <Stack key={idx} direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        {group.map((p) => renderParam(p))}
                      </Stack>
                    );
                  });
                })()}
                {(() => {
                  const totalEntry = test?.resultData?.find(
                    (r) => r.kind === "brefScore" && r.name === "total"
                  );
                  if (!totalEntry?.value) return null;
                  return (
                    <Box
                      sx={{
                        alignSelf: "flex-start",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "primary.main",
                      }}
                    >
                      <Typography variant="body2" color="primary">
                        Total = {totalEntry.value}/{totalEntry.denom}
                      </Typography>
                    </Box>
                  );
                })()}
                {(() => {
                  const portesT = test?.portesTotal;
                  if (!portesT?.pc) return null;
                  return (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: "flex-start" }}>
                      <Typography variant="body2" color="textSecondary">
                        Total = {portesT.raw}
                      </Typography>
                      <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                        <Typography variant="body2" color="primary">
                          {formatPortesPC(portesT.pc)}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                })()}
                {(() => {
                  if (testParams.evalRule !== "vosp") return null;
                  const age = calcAge(patientInfo.dateNaissance, patientInfo.dateExamen);
                  const status = getVospStatus(testParams.params, age);
                  if (!status) return null;
                  return (
                    <Box
                      sx={{
                        alignSelf: "flex-start",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        backgroundColor: "primary.main",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "primary.contrastText", fontWeight: 600 }}>
                        {status === "fail" ? "Fail" : "Pass"}
                      </Typography>
                    </Box>
                  );
                })()}
                {testParams.ceradScale && test?.resultData?.some((r) => r.name !== "commentaire") && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ alignSelf: "flex-start" }}>
                    {test.resultData
                      .filter((r) => r.name !== "commentaire")
                      .map((r, i) => (
                        <Box key={r.name || i} sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: r.error ? "warning.main" : "primary.main" }}>
                          <Typography variant="body2" color={r.error ? "warning.main" : "primary"}>
                            {r.name === "comment" ? r.value : (r.error ? r.value : `${r.label} = ${r.value}`)}
                          </Typography>
                        </Box>
                      ))}
                  </Stack>
                )}
              </Stack>
              {testParams.params.some((p) => p.name === "commentaire") && (() => {
                const commentParam = testParams.params.find((p) => p.name === "commentaire");
                return (
                  <TextField
                    label={commentParam.displayLabel || t("test.clinicalNote")}
                    placeholder={commentParam.placeholder || undefined}
                    InputLabelProps={commentParam.placeholder ? { shrink: true } : undefined}
                    size="small"
                    multiline
                    minRows={2}
                    value={commentParam.value || ""}
                    onChange={(e) => handleParamChange(commentParam.name, e.target.value)}
                    fullWidth
                  />
                );
              })()}
            </Stack>
          )}
          {testParams.type === "parquet" && testParams.params?.some((p) => p.type === "input") && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
              {testParams.params
                .filter((p) => p.type === "input")
                .map((param) => (
                  <TextField
                    key={param.name}
                    label={param.label || param.name}
                    size="small"
                    value={param.value || ""}
                    onChange={(e) => handleParamChange(param.name, e.target.value)}
                    sx={{ minWidth: 260 }}
                  />
                ))}
              {test?.resultData?.map((result, i) => (
                <Box key={i} sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: result.error ? "warning.main" : "primary.main" }}>
                  <Typography variant="body2" color={result.error ? "warning.main" : "primary"}>
                    {result.error ? result.value : `${result.label} = ${result.value}`}
                  </Typography>
                </Box>
              ))}
              {test?.results?.flatMap((entry) => entry.resultData || []).map((result, i) => (
                <Box key={i} sx={{ px: 1.5, py: 0.5, borderRadius: 2, border: "1px solid", borderColor: "primary.main" }}>
                  <Typography variant="body2" color="primary" >
                    {result.label} = {result.value}{result.tScore ? ` (T=${result.tScore})` : ""}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {ageBoundWarning && (
            <Alert severity="warning">
              {ageBoundWarning}
            </Alert>
          )}
          {warning && (
            <Alert severity="warning" onClose={() => setWarning(null)}>
              {warning}
            </Alert>
          )}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Stack>
      </SecondaryColoredBorderCard>
      <RtfUploadDialog
        open={rtfDialogOpen}
        handleClose={() => setRtfDialogOpen(false)}
        testName={name}
      />
      <CommentDialog
        open={commentDialogOpen}
        handleClose={() => setCommentDialogOpen(false)}
        testName={name}
      />
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          <Typography variant="h6" color="error">
            {t("test.reset")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t("test.resetConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResetDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => { resetTest(name); setResetDialogOpen(false); }}
            variant="contained"
            color="error"
          >
            {t("test.reset")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default memo(TestCard);
